/**
 * 회원 관리 컴포넌트 (DDD 리팩토링 버전)
 * UI 렌더링만 담당, 비즈니스 로직은 use-cases/services로 분리
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  PencilIcon,
  ClockIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  TrashIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  NoSymbolIcon,
  InformationCircleIcon,
  AcademicCapIcon,
  UserIcon,
  TagIcon,
} from '@heroicons/react/24/outline';

import { useMemberData } from '../use-cases';
import MemberBasicInfo2Tab from './member/MemberBasicInfo2Tab';
import MemberMonthlyScheduleView from './MemberMonthlyScheduleView';
import { getMembershipBadge, getStatusBadge } from '../utils';
import { expiryDateKST, formatDateTimeKST } from '../../../utils/dateUtils';
import { formatExceptionTime } from '../../../utils/exceptionTime';
import { memberService } from '../services/MemberService';
import type { Member, NewMemberData, MembershipType, MemberStatus } from '../models';
import { API_BASE_URL } from '../../../config/api';
import { apiClient } from '../../../infrastructure/api';
import SelfRegistrationPending from './SelfRegistrationPending';
import RestoreCompareModal, { isValidMobile, maskPhone } from './member/RestoreCompareModal';
import type { DeletedMemberExistsError } from '../repositories/MemberRepository';
import * as DashboardService from '../services/DashboardService';
import { type UserPayment } from '../../../services/pricingApi';
import { studentWithParentManagementRepository } from '../repositories/StudentWithParentManagementRepository';
import { memberRepository } from '../repositories';
import { getTodayKST, formatDateKST } from '../../../utils/dateUtils';
import PenaltyDetailModal from './penalty/PenaltyDetailModal';
import { penaltySourceBadge } from '../utils/penaltySource';

type MemberSortOrder =
  | 'registered_desc'
  | 'registered_asc'
  | 'name'
  | 'name_desc'
  | 'seat_asc'
  | 'seat_desc';

const MEMBER_SORT_OPTIONS: { value: MemberSortOrder; label: string }[] = [
  { value: 'registered_desc', label: '최신 등록순' },
  { value: 'registered_asc', label: '등록순 (오래된순)' },
  { value: 'name', label: '이름순 (가나다)' },
  { value: 'name_desc', label: '이름 역순 (하나가)' },
  { value: 'seat_asc', label: '좌석번호 낮은순' },
  { value: 'seat_desc', label: '좌석번호 높은순' },
];

/**
 * 정렬은 이 PC(브라우저)에 기억한다. 매장에 PC 가 여러 대이고 보는 사람이 달라서,
 * 서버 한 곳에 묶으면 서로 덮어쓴다. PC 가 아직 고른 적이 없을 때만 매장 기본값을 쓴다.
 * 검색어·재원생/퇴원생 보기는 기억하지 않는다 — 퇴원생 화면으로 열리면 학생이 사라진 것처럼 보인다.
 */
const MEMBER_SORT_STORAGE_KEY = 'newattend.memberList.sortOrder';

const isMemberSortOrder = (v: unknown): v is MemberSortOrder =>
  typeof v === 'string' && MEMBER_SORT_OPTIONS.some((o) => o.value === v);

const readStoredSortOrder = (): MemberSortOrder | null => {
  try {
    const saved = window.localStorage.getItem(MEMBER_SORT_STORAGE_KEY);
    return isMemberSortOrder(saved) ? saved : null;
  } catch {
    return null;
  }
};

const MemberManagement: React.FC = () => {
  // Method 한글 표시 함수
  const formatMethod = (method: string | undefined) => {
    if (!method) return 'N/A';
    switch (method) {
      case 'face':
        return '얼굴 인식';
      case 'phone':
        return '전화번호';
      case 'qr_code':
      case 'qr':
        return 'QR';
      default:
        return method;
    }
  };

  // 외출 유형 한글 표시 함수
  const getOutingTypeLabel = (outingType: string | null | undefined): string => {
    if (!outingType) return '';
    const labels: Record<string, string> = {
      meal: '식사',
      rest: '휴식',
      hospital: '병원',
      academy: '학원',
      toilet: '화장실',
      other: '기타',
    };
    return labels[outingType] || outingType;
  };

  /**
   * 날짜/시간 표시 — 항상 KST.
   * 백엔드가 이미 KST 로 포맷해 준 문자열("2025.11.05 (화) 10:18")은 그대로 쓰고,
   * ISO 문자열은 UTC 로 내려오므로 KST 로 변환한다(그냥 자르면 9시간 어긋난다).
   */
  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return '미설정';
    if (dateString.includes('(')) return dateString;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    return formatDateTimeKST(dateString) ?? dateString;
  };

  /**
   * 만료일(= 마지막 이용 가능일 23:59:59 KST)을 "YYYY. M. D." 로
   */
  const formatExpiryDate = (dateString: string | undefined) => {
    const ymd = expiryDateKST(dateString);
    if (!ymd) return dateString || '미설정';
    const [y, m, d] = ymd.split('-');
    return `${y}. ${Number(m)}. ${Number(d)}.`;
  };

  /** 만료일을 "YYYY-MM-DD"(KST) 로 — 목록 배지·엑셀 표기를 상세와 통일 */
  const formatExpiryYMD = (dateString: string | undefined): string | null =>
    expiryDateKST(dateString);

  // DDD 훅 사용
  const {
    members,
    selectedMember,
    loading,
    loadingDetail,
    error,
    totalMembers,
    loadMembers,
    loadMemberDetail,
    createMember,
    deleteMemberFace,
    filterMembers,
    setSelectedMember,
  } = useMemberData();

  // 로컬 UI 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | MemberStatus>('all');
  const [filterMembership, setFilterMembership] = useState<'all' | MembershipType>('all');
  // 재원생(active) / 퇴원생(withdrawn) 보기 전환
  const [viewMode, setViewMode] = useState<'active' | 'withdrawn'>('active');
  const [sortOrder, setSortOrder] = useState<MemberSortOrder>(
    () => readStoredSortOrder() ?? 'registered_desc'
  );
  const [savingSortDefault, setSavingSortDefault] = useState(false);
  const [sortDefaultMsg, setSortDefaultMsg] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [counselingRecords, setCounselingRecords] = useState<any[]>([]);
  const [studyTimeData, setStudyTimeData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayKST());
  const [isLoadingStudyData, setIsLoadingStudyData] = useState(false);
  const [weeklyStudyData, setWeeklyStudyData] = useState<any[]>([]);
  const [isLoadingWeeklyData, setIsLoadingWeeklyData] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<UserPayment[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [extensionHistory, setExtensionHistory] = useState<any[]>([]);

  // 학부모 초대 관련 state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Member | null>(null);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // 학부모 연락처 존재 여부 체크
  const hasParentPhone = (student: Member | null) =>
    !!(student && (student.parent_phone_1 || student.parent_phone_2));

  // 입장 차단 설정 상태
  const [blockExpiredEntry, setBlockExpiredEntry] = useState<boolean | null>(null);

  // 페이지네이션 state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [newMember, setNewMember] = useState<NewMemberData>({
    name: '',
    phone: '',
    parent_phone_1: '',
    parent_phone_2: '',
  });
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  // 부모 알림 필드 state
  const [isEditingParentPhone1, setIsEditingParentPhone1] = useState(false);
  const [newParentPhone1, setNewParentPhone1] = useState('');
  const [isEditingParentPhone2, setIsEditingParentPhone2] = useState(false);
  const [newParentPhone2, setNewParentPhone2] = useState('');
  // 퇴원 회원 복구 전 비교 창 (2026-10-28 대표 지시)
  const [restoreConflict, setRestoreConflict] = useState<DeletedMemberExistsError | null>(null);
  // 학부모 연락처 변경·삭제 확인 (창 안 확인 — window.confirm 금지, 2026-10-28)
  const [parentPhoneConfirm, setParentPhoneConfirm] = useState<{ slot: 1 | 2; oldVal: string; newVal: string } | null>(null);
  // 학부모 이름 편집 state
  const [isEditingParent1Name, setIsEditingParent1Name] = useState(false);
  const [newParent1Name, setNewParent1Name] = useState('');
  const [isEditingParent2Name, setIsEditingParent2Name] = useState(false);
  const [newParent2Name, setNewParent2Name] = useState('');

  // 좌석 배정 state
  const [isEditingSeat, setIsEditingSeat] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newSeatId, setNewSeatId] = useState('');
  const [availableSeats, setAvailableSeats] = useState<Array<{id: string; seat_number: number; seat_code: string}>>([]);

  // 사물함 state (2026-09-02 신설)
  // 🔴 좌석과 완전히 별개다. 사물함을 줘도 좌석 배정은 그대로 유지된다
  //    (좌석으로 사물함을 만들면 자리가 풀리던 문제 때문에 별도 표로 분리했다).
  const [lockerNumber, setLockerNumber] = useState<number | null>(null);
  const [isEditingLocker, setIsEditingLocker] = useState(false);
  const [newLockerNumber, setNewLockerNumber] = useState('');
  const [lockerMsg, setLockerMsg] = useState('');

  // 학생 정보 편집 state
  const [isEditingSchool, setIsEditingSchool] = useState(false);
  const [newSchool, setNewSchool] = useState('');
  const [isEditingGrade, setIsEditingGrade] = useState(false);
  const [newGrade, setNewGrade] = useState('');
  const [isEditingClassName, setIsEditingClassName] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [isEditingGender, setIsEditingGender] = useState(false);
  const [newGender, setNewGender] = useState('');
  // 생년월일/회원구분 (basic2 → 기본정보 1로 이전)
  const [isEditingBirthDate, setIsEditingBirthDate] = useState(false);
  const [newBirthDate, setNewBirthDate] = useState('');
  const [isEditingMemberCategory, setIsEditingMemberCategory] = useState(false);
  const [newMemberCategory, setNewMemberCategory] = useState('');

  // 엑셀 일괄 등록 state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showBulkResultModal, setShowBulkResultModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bulkUploadResult, setBulkUploadResult] = useState<{
    total_rows: number;
    success: number;
    created_count: number;
    updated_count: number;
    failed: number;
    errors: Array<{
      row: number;
      field: string;
      code: string;
      message: string;
    }>;
    created_member_ids: string[];
    updated_members: Array<{
      row: number;
      name: string;
      phone: string;
      message: string;
    }>;
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // 상세보기 모달 탭 state
  const [activeTab, setActiveTab] = useState<'basic' | 'basic2' | 'parent' | 'attendance' | 'ticket' | 'schedule' | 'patrol' | 'penalty' | 'exception' | 'study' | 'communication' | 'memo'>('basic');

  // 사유서 탭 state
  const [exceptionRecords, setExceptionRecords] = useState<Array<{
    id: string;
    exception_date: string;
    exception_type: string | null;
    reason: string | null;
    status: string;
    submitter_role?: string | null;
    requested_by_name?: string | null;
    review_note?: string | null;
    reviewed_at?: string | null;
    created_at?: string | null;
    expected_arrival_time?: string | null;
    expected_departure_time?: string | null;
  }>>([]);
  const [isLoadingException, setIsLoadingException] = useState(false);

  // 스케줄 데이터 state
  const [scheduleData, setScheduleData] = useState<{
    schedule_type: string;
    study_schedules: Array<{id: string; day_of_week: number; day_name: string; expected_check_in: string; expected_check_out: string; is_active: boolean}>;
    academy_schedules: Array<{id: string; academy_name: string; days_of_week: number[]; start_time: string; end_time: string; is_today: boolean}>;
    period_blocks: Array<{student_id: string; student_name: string; day_of_week: number; periods: number[]; start_time: string; end_time: string}>;
  }>({ schedule_type: 'weekly', study_schedules: [], academy_schedules: [], period_blocks: [] });
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  // ⭐ 2026-06-14: 학생이 입력한 시간범위 (요일별) — 스케줄 탭에 "등록 시간" 표시용
  const [scheduleRanges, setScheduleRanges] = useState<Record<string, { start: string; end: string }[]>>({});
  const [periodTemplates, setPeriodTemplates] = useState<Array<{
    period_number: number;
    period_name: string;
    period_type: string;
    start_time: string;
    end_time: string;
    display_order: number;
    is_active: boolean;
  }>>([]);

  // 순찰 기록 state
  const [patrolNotes, setPatrolNotes] = useState<Array<{
    id: string;
    seat_code: string;
    note_type: string;
    note_type_label: string;
    note_text: string;
    staff_name: string;
    created_at: string;
  }>>([]);
  const [patrolStats, setPatrolStats] = useState<{total_count: number; type_counts: Record<string, number>}>({ total_count: 0, type_counts: {} });
  const [isLoadingPatrol, setIsLoadingPatrol] = useState(false);

  // 벌점 데이터 state
  const [penaltyRecords, setPenaltyRecords] = useState<Array<{
    id: string;
    penalty_type: string;
    points: number;
    reason: string;
    occurred_date: string;
    is_auto: boolean;
    is_cancelled: boolean;
    reference_type?: string | null;
  }>>([]);
  // 벌점 상세보기 모달 (벌점 탭에서 한 건 클릭)
  const [penaltyDetailId, setPenaltyDetailId] = useState<string | null>(null);
  const [penaltySummary, setPenaltySummary] = useState<{current_points: number; total_points_ever: number} | null>(null);
  const [isLoadingPenalty, setIsLoadingPenalty] = useState(false);

  // 지각 기록 state
  const [lateRecords, setLateRecords] = useState<Array<{
    id: number;
    student_id: string;
    record_date: string;
    expected_time: string;
    actual_time: string;
    minutes_late: number;
    penalty_issued: boolean;
    created_at: string | null;
  }>>([]);

  // ⭐ 2026-06-14: 출결 탭 전용 지각 이력 (벌점 탭과 분리, 더보기로 기간 확장)
  // ⭐ 2026-06-21: 교시별 지각 이력 (날짜별, 쉬는시간 복귀 지각 포함)
  type LateDay = { date: string; total_minutes: number; periods: { period_number: number; period_label: string; expected: string; actual: string; minutes_late: number }[] };
  const [attLateRecords, setAttLateRecords] = useState<LateDay[]>([]);
  const [attLateDays, setAttLateDays] = useState<number>(30);
  const [attLateLoading, setAttLateLoading] = useState<boolean>(false);

  // 학습관리 히스토리 state (오늘 데이터는 기존 studyTimeData 사용)
  const [studyTimeHistory, setStudyTimeHistory] = useState<Array<{
    date: string;
    total_minutes: number;
  }>>([]);

  // ⭐ 2026-07-27 학습관리 탭 개편 (대전월평점 제보)
  //   - daily_study_time 단일 기준으로 읽어 전광판·랭킹과 숫자 일치
  //   - 실패를 '데이터 없음'과 구분해 표시 (기존엔 요청 하나 실패 = 통째로 '없음')
  //   - 기간 선택 추가 (점주 요청)
  const [studyRange, setStudyRange] = useState<'7d' | '30d' | 'month' | 'custom'>('7d');
  const [studyStart, setStudyStart] = useState<string>('');
  const [studyEnd, setStudyEnd] = useState<string>('');
  const [studySummary, setStudySummary] = useState<{
    start_date: string; end_date: string; total_minutes: number;
    study_days: number; total_days: number; avg_minutes_on_study_days: number;
    today_minutes: number;
    days: Array<{ date: string; total_minutes: number; session_count: number }>;
  } | null>(null);
  const [studyError, setStudyError] = useState<string | null>(null);

  // 학부모소통 state
  const [commPosts, setCommPosts] = useState<Array<{
    id: string;
    post_type: string;
    target_student_id: string | null;
    target_student_name: string | null;
    title: string;
    content: string;
    author_name: string;
    is_pinned: boolean;
    read_count: number;
    comment_count: number;
    created_at: string;
  }>>([]);
  const [isLoadingComm, setIsLoadingComm] = useState(false);
  const [commNewTitle, setCommNewTitle] = useState('');
  const [commNewContent, setCommNewContent] = useState('');
  const [commNewType, setCommNewType] = useState<'individual' | 'announcement'>('individual');
  const [isSubmittingComm, setIsSubmittingComm] = useState(false);
  const [commSelectedPost, setCommSelectedPost] = useState<any>(null);
  const [commComments, setCommComments] = useState<Array<{
    id: string;
    author_type: string;
    author_name: string;
    content: string;
    created_at: string;
  }>>([]);
  const [commNewComment, setCommNewComment] = useState('');

  // 회원 메모 state
  const [memos, setMemos] = useState<Array<{
    id: string;
    student_id: string;
    author_id: string;
    author_name: string;
    category: string;
    content: string;
    is_pinned: boolean;
    created_at: string;
    updated_at: string;
  }>>([]);
  const [memosTotal, setMemosTotal] = useState(0);
  const [isLoadingMemos, setIsLoadingMemos] = useState(false);
  const [newMemoContent, setNewMemoContent] = useState('');
  const [newMemoCategory, setNewMemoCategory] = useState('general');
  const [editingMemoId, setEditingMemoId] = useState<string | null>(null);
  const [editMemoContent, setEditMemoContent] = useState('');

  // 초기 데이터 로드
  useEffect(() => {
    loadMembers({ page: 1, limit: itemsPerPage });
    loadAvailableSeats();
    loadIntegrationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 연동 설정 로드 (입장 차단 설정 확인용)
  const loadIntegrationSettings = async () => {
    try {
      const response = await apiClient.get('/api/v1/owner/integration-settings');
      setBlockExpiredEntry(response.data.block_expired_entry ?? false);
    } catch (error) {
      console.error('[회원관리] 연동 설정 로드 실패:', error);
    }
  };

  // 정렬 변경 — 이 PC 에 기억해 다음에 열 때도 같은 순서로 보이게 한다
  const changeSortOrder = (value: MemberSortOrder) => {
    setSortOrder(value);
    setSortDefaultMsg(null);
    try {
      window.localStorage.setItem(MEMBER_SORT_STORAGE_KEY, value);
    } catch {
      // 브라우저가 저장을 막아 둔 경우 — 이번 화면에서만 적용되고 끝난다
    }
  };

  // 지금 정렬을 매장 기본값으로 저장 (다른 PC 가 처음 열 때 쓰인다)
  const saveSortAsStoreDefault = async () => {
    setSavingSortDefault(true);
    setSortDefaultMsg(null);
    try {
      await apiClient.put('/api/v1/rules/member-list', { sort_order: sortOrder });
      setSortDefaultMsg('매장 기본 순서로 저장했습니다.');
    } catch (error) {
      console.error('[회원관리] 기본 정렬 저장 실패:', error);
      setSortDefaultMsg('저장하지 못했습니다.');
    } finally {
      setSavingSortDefault(false);
    }
  };

  // 이 PC 가 아직 정렬을 고른 적이 없으면 매장 기본값으로 시작한다
  useEffect(() => {
    if (readStoredSortOrder()) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient.get('/api/v1/rules');
        const saved = response.data?.member_list?.sort_order;
        if (!cancelled && isMemberSortOrder(saved) && saved !== 'registered_desc') {
          setSortOrder(saved);
        }
      } catch (error) {
        console.error('[회원관리] 매장 기본 정렬 조회 실패:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 빈 좌석 목록 로드 (미배정 좌석만). studentId 주면 그 회원의 현재 좌석도 포함(상세보기 유지용)
  const loadAvailableSeats = async (studentId?: string) => {
    try {
      const url = studentId
        ? `/v1/owner/available-seats?include_student_id=${encodeURIComponent(studentId)}`
        : '/v1/owner/available-seats';
      const response = await apiClient.get(url);
      setAvailableSeats(response.data.seats || []);
    } catch (error) {
      console.error('좌석 목록 로드 실패:', error);
    }
  };

  // 스케줄 데이터 로드
  useEffect(() => {
    if (selectedMember && activeTab === 'schedule') {
      const fetchScheduleData = async () => {
        setIsLoadingSchedule(true);
        try {
          const response = await apiClient.get(
            `/v1/owner/student-schedules/student/${selectedMember.id}/all`
          );
          const result = response.data;
          if (result.success && result.data) {
            setScheduleData({
              schedule_type: result.data.schedule_type || 'weekly',
              study_schedules: result.data.study_schedules || [],
              academy_schedules: result.data.academy_schedules || [],
              period_blocks: result.data.period_blocks || []
            });
          }

          // 교시 템플릿 로드
          try {
            const tplRes = await apiClient.get('/v1/owner/period-templates');
            const tplData = tplRes.data?.data || tplRes.data || [];
            setPeriodTemplates(Array.isArray(tplData) ? tplData : []);
          } catch {
            setPeriodTemplates([]);
          }

          // ⭐ 학생이 입력한 시간(ranges) 로드 — 교시와 별개로 "등록 시간" 표시
          try {
            const rangeRes = await apiClient.get(
              `/v1/owner/student-schedules/student/${selectedMember.id}/period-schedules`
            );
            setScheduleRanges(rangeRes.data?.ranges || {});
          } catch {
            setScheduleRanges({});
          }
        } catch (error) {
          console.error('Failed to fetch schedule data:', error);
          setScheduleData({ schedule_type: 'weekly', study_schedules: [], academy_schedules: [], period_blocks: [] });
        } finally {
          setIsLoadingSchedule(false);
        }
      };
      fetchScheduleData();
    }
  }, [selectedMember, activeTab]);

  // 순찰 기록 로드
  useEffect(() => {
    if (selectedMember && activeTab === 'patrol') {
      const fetchPatrolNotes = async () => {
        setIsLoadingPatrol(true);
        try {
          const response = await apiClient.get(
            `/v1/owner/student-schedules/student/${selectedMember.id}/patrol-notes?days=30`
          );
          const result = response.data;
          if (result.success && result.data) {
            setPatrolNotes(result.data.notes || []);
            setPatrolStats({
              total_count: result.data.total_count || 0,
              type_counts: result.data.type_counts || {}
            });
          }
        } catch (error) {
          console.error('Failed to fetch patrol notes:', error);
          setPatrolNotes([]);
          setPatrolStats({ total_count: 0, type_counts: {} });
        } finally {
          setIsLoadingPatrol(false);
        }
      };
      fetchPatrolNotes();
    }
  }, [selectedMember, activeTab]);

  // 벌점 기록 로드
  useEffect(() => {
    if (selectedMember && activeTab === 'penalty') {
      const fetchPenaltyData = async () => {
        setIsLoadingPenalty(true);
        try {
          const orgId = localStorage.getItem('organization_id') || localStorage.getItem('organizationId');

          // 학생 ID (user_id 우선, 없으면 id 사용)
          const studentId = selectedMember.user_id || selectedMember.id;

          // 디버그 로깅
          console.log('[Penalty] Fetching data for:', {
            studentId,
            orgId,
            selectedMemberId: selectedMember.id,
            selectedMemberUserId: selectedMember.user_id,
          });

          // 벌점 기록 조회 (서버는 {items, total, page, page_size} 페이지네이션 응답)
          try {
            const recordsResponse = await apiClient.get(
              `/api/penalties/records?organization_id=${orgId}&student_id=${studentId}&include_cancelled=true&page_size=100`
            );
            const body = recordsResponse.data;
            const items = Array.isArray(body) ? body : (body?.items || []);
            setPenaltyRecords(items);
          } catch (recordsError) {
            console.error('[Penalty] Records error:', recordsError);
            setPenaltyRecords([]);
          }

          // 벌점 현황 조회
          try {
            const summaryResponse = await apiClient.get(
              `/api/penalties/summaries/${studentId}?organization_id=${orgId}`
            );
            setPenaltySummary(summaryResponse.data);
          } catch (summaryError) {
            console.error('[Penalty] Summary error:', summaryError);
            setPenaltySummary(null);
          }

          // 지각 기록 조회
          try {
            const lateResponse = await apiClient.get(
              `/v1/owner/attendance-monitoring/late-records?student_id=${studentId}&organization_id=${orgId}&days=30`
            );
            setLateRecords(lateResponse.data || []);
          } catch (lateError) {
            console.error('[Penalty] Late records error:', lateError);
            setLateRecords([]);
          }
        } catch (error) {
          console.error('Failed to fetch penalty data:', error);
          setPenaltyRecords([]);
          setPenaltySummary(null);
          setLateRecords([]);
        } finally {
          setIsLoadingPenalty(false);
        }
      };
      fetchPenaltyData();
    }
  }, [selectedMember, activeTab]);

  // 사유서 탭 로드 — 해당 학생의 사유서 이력 전체
  useEffect(() => {
    if (selectedMember && activeTab === 'exception') {
      const fetchExceptions = async () => {
        setIsLoadingException(true);
        try {
          const studentId = selectedMember.user_id || selectedMember.id;
          const res = await apiClient.get(
            `/v1/owner/schedule-exceptions/requests?status=all&student_id=${studentId}`
          );
          setExceptionRecords(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
          console.error('[Exception] 사유서 이력 로드 실패:', error);
          setExceptionRecords([]);
        } finally {
          setIsLoadingException(false);
        }
      };
      fetchExceptions();
    }
  }, [selectedMember, activeTab]);

  // ⭐ 출결 탭 지각 이력 로드 (벌점 탭과 분리, attLateDays 변경 시 재조회 = 더보기)
  useEffect(() => {
    if (selectedMember && activeTab === 'attendance') {
      const fetchAttLate = async () => {
        setAttLateLoading(true);
        try {
          const studentId = selectedMember.user_id || selectedMember.id;
          // ⭐ 교시별 지각 이력 (쉬는시간 복귀 포함) — late-history 엔드포인트
          const res = await apiClient.get(
            `/v1/owner/student-schedules/student/${studentId}/late-history?days=${attLateDays}`
          );
          setAttLateRecords(res.data?.history || []);
        } catch (e) {
          console.error('[Attendance] Late history error:', e);
          setAttLateRecords([]);
        } finally {
          setAttLateLoading(false);
        }
      };
      fetchAttLate();
    }
  }, [selectedMember, activeTab, attLateDays]);

  // 선택한 기간 → 시작/종료일 (KST)
  const resolveStudyRange = useCallback((): { start: string; end: string } => {
    const end = getTodayKST();
    if (studyRange === 'custom' && studyStart && studyEnd) {
      return { start: studyStart, end: studyEnd };
    }
    if (studyRange === 'month') {
      return { start: `${end.slice(0, 7)}-01`, end };
    }
    const back = studyRange === '30d' ? 29 : 6;
    return { start: formatDateKST(new Date(Date.now() - back * 24 * 60 * 60 * 1000)), end };
  }, [studyRange, studyStart, studyEnd]);

  // 학습 데이터 로드 (학습관리 탭) — daily_study_time 단일 기준
  useEffect(() => {
    if (!selectedMember || activeTab !== 'study') return;
    const fetchStudyData = async () => {
      setIsLoadingStudyData(true);
      setStudyError(null);
      try {
        const userId = selectedMember.user_id || selectedMember.id;
        const { start, end } = resolveStudyRange();
        const res = await apiClient.get(
          `/api/v1/owner/members/${userId}/study-summary?start_date=${start}&end_date=${end}`
        );
        setStudySummary(res.data);
        setStudyTimeHistory((res.data?.days || []).slice().reverse());
      } catch (error: any) {
        console.error('Failed to fetch study summary:', error);
        setStudySummary(null);
        setStudyTimeHistory([]);
        // 실패를 '데이터 없음'으로 보여주지 않는다 — 점주가 데이터가 없는 걸로 오해했던 사례
        setStudyError(
          error?.response?.data?.detail || '학습 데이터를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.'
        );
      } finally {
        setIsLoadingStudyData(false);
      }
    };
    fetchStudyData();
  }, [selectedMember, activeTab, resolveStudyRange]);

  // 학부모소통 데이터 로드
  const loadCommPosts = async (studentId: string) => {
    setIsLoadingComm(true);
    try {
      const response = await apiClient.get(
        `/api/v1/owner/posts?target_student_id=${studentId}&limit=50`
      );
      setCommPosts(response.data?.data?.posts || []);
    } catch (error) {
      console.error('[Communication] Posts load error:', error);
      setCommPosts([]);
    } finally {
      setIsLoadingComm(false);
    }
  };

  const loadCommPostDetail = async (postId: string) => {
    try {
      const response = await apiClient.get(`/api/v1/owner/posts/${postId}`);
      const data = response.data?.data;
      if (data) {
        setCommSelectedPost(data.post);
        setCommComments(data.comments || []);
      }
    } catch (error) {
      console.error('[Communication] Post detail error:', error);
    }
  };

  const handleCommSubmitPost = async () => {
    if (!commNewTitle.trim() || !commNewContent.trim() || !selectedMember) return;
    setIsSubmittingComm(true);
    try {
      const studentId = selectedMember.user_id || selectedMember.id;
      await apiClient.post('/api/v1/owner/posts', {
        post_type: commNewType,
        target_student_id: commNewType === 'individual' ? studentId : null,
        title: commNewTitle,
        content: commNewContent,
        is_pinned: false,
      });
      setCommNewTitle('');
      setCommNewContent('');
      loadCommPosts(studentId);
    } catch (error) {
      console.error('[Communication] Post submit error:', error);
      alert('글 작성에 실패했습니다.');
    } finally {
      setIsSubmittingComm(false);
    }
  };

  const handleCommSubmitComment = async () => {
    if (!commNewComment.trim() || !commSelectedPost) return;
    try {
      await apiClient.post(`/api/v1/owner/posts/${commSelectedPost.id}/comments`, {
        content: commNewComment,
      });
      setCommNewComment('');
      loadCommPostDetail(commSelectedPost.id);
    } catch (error) {
      console.error('[Communication] Comment submit error:', error);
      alert('댓글 작성에 실패했습니다.');
    }
  };

  useEffect(() => {
    if (selectedMember && activeTab === 'communication') {
      const studentId = selectedMember.user_id || selectedMember.id;
      loadCommPosts(studentId);
      setCommSelectedPost(null);
      setCommComments([]);
    }
  }, [selectedMember, activeTab]);

  // 메모 로드 (메모 탭)
  const loadMemos = async (studentId: string, offset = 0) => {
    setIsLoadingMemos(true);
    try {
      const response = await apiClient.get(`/api/v1/owner/member-memos?student_id=${studentId}&limit=20&offset=${offset}`);
      if (response.data?.success) {
        if (offset === 0) {
          setMemos(response.data.data.memos);
        } else {
          setMemos(prev => [...prev, ...response.data.data.memos]);
        }
        setMemosTotal(response.data.data.total);
      }
    } catch (error) {
      console.error('[메모] 로드 실패:', error);
    } finally {
      setIsLoadingMemos(false);
    }
  };

  useEffect(() => {
    if (selectedMember && activeTab === 'memo') {
      const studentId = selectedMember.user_id || selectedMember.id;
      loadMemos(studentId);
    }
  }, [selectedMember, activeTab]);

  const handleMemoSubmit = async () => {
    if (!newMemoContent.trim() || !selectedMember) return;
    const studentId = selectedMember.user_id || selectedMember.id;
    try {
      await apiClient.post('/api/v1/owner/member-memos', {
        student_id: studentId,
        content: newMemoContent.trim(),
        category: newMemoCategory,
      });
      setNewMemoContent('');
      setNewMemoCategory('general');
      loadMemos(studentId);
    } catch (error) {
      console.error('[메모] 작성 실패:', error);
      alert('메모 작성에 실패했습니다.');
    }
  };

  const handleMemoDelete = async (memoId: string) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;
    try {
      await apiClient.delete(`/api/v1/owner/member-memos/${memoId}`);
      const studentId = selectedMember?.user_id || selectedMember?.id;
      if (studentId) loadMemos(studentId);
    } catch (error) {
      console.error('[메모] 삭제 실패:', error);
      alert('메모 삭제에 실패했습니다.');
    }
  };

  const handleMemoEdit = async (memoId: string) => {
    if (!editMemoContent.trim()) return;
    try {
      await apiClient.patch(`/api/v1/owner/member-memos/${memoId}`, {
        content: editMemoContent.trim(),
      });
      setEditingMemoId(null);
      setEditMemoContent('');
      const studentId = selectedMember?.user_id || selectedMember?.id;
      if (studentId) loadMemos(studentId);
    } catch (error) {
      console.error('[메모] 수정 실패:', error);
      alert('메모 수정에 실패했습니다.');
    }
  };

  const handleMemoPinToggle = async (memoId: string, currentPinned: boolean) => {
    try {
      await apiClient.patch(`/api/v1/owner/member-memos/${memoId}`, {
        is_pinned: !currentPinned,
      });
      const studentId = selectedMember?.user_id || selectedMember?.id;
      if (studentId) loadMemos(studentId);
    } catch (error) {
      console.error('[메모] 고정 변경 실패:', error);
    }
  };

  const MEMO_CATEGORIES: Record<string, string> = {
    general: '일반',
    academic: '학업',
    behavior: '행동',
    health: '건강',
    family: '가정',
    other: '기타',
  };

  const MEMO_CATEGORY_COLORS: Record<string, string> = {
    general: 'bg-gray-100 text-gray-700',
    academic: 'bg-blue-100 text-blue-700',
    behavior: 'bg-orange-100 text-orange-700',
    health: 'bg-green-100 text-green-700',
    family: 'bg-purple-100 text-purple-700',
    other: 'bg-yellow-100 text-yellow-700',
  };

  // 결제 내역 로드 (이용권 탭) - payment_vouchers 테이블에서 조회
  useEffect(() => {
    if (selectedMember && activeTab === 'ticket') {
      const fetchPaymentHistory = async () => {
        setIsLoadingPayments(true);
        try {
          // user_id 우선, 없으면 id 사용
          const userId = selectedMember.user_id || selectedMember.id;

          const response = await apiClient.get(
            `/v1/owner/payment-management/vouchers/user/${userId}`
          );
          const result = response.data;
          if (result.success && result.data) {
            setPaymentHistory(result.data);
            // 각 이용권의 연기 이력 조회
            const allExtensions: any[] = [];
            for (const voucher of result.data) {
              try {
                const extRes = await apiClient.get(
                  `/v1/owner/payment-management/vouchers/${voucher.payment_id}/extensions`
                );
                if (extRes.data?.extensions) {
                  allExtensions.push(...extRes.data.extensions.map((ext: any) => ({
                    ...ext,
                    voucher_name: voucher.plan_name,
                  })));
                }
              } catch {}
            }
            setExtensionHistory(allExtensions);
          } else {
            setPaymentHistory([]);
            setExtensionHistory([]);
          }
        } catch (error) {
          console.error('[Payment] Failed to fetch vouchers:', error);
          setPaymentHistory([]);
          setExtensionHistory([]);
        } finally {
          setIsLoadingPayments(false);
        }
      };
      fetchPaymentHistory();
    }
  }, [selectedMember, activeTab]);

  // 서버 페이지네이션 사용 - members가 이미 페이지네이션된 데이터
  // 퇴원(inactive) 학생은 백엔드에서 맨 뒤로 정렬됨
  const paginatedMembers = members;

  // 페이지네이션 계산 (서버 기준)
  const totalPages = Math.ceil(totalMembers / itemsPerPage);

  // 필터/검색 변경 시 서버에 새 요청
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setCurrentPage(1);
      loadMembers({
        page: 1,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        membershipType: filterMembership !== 'all' ? filterMembership : undefined,
        withdrawn: viewMode === 'withdrawn',
        sort: sortOrder,
      });
    }, 300); // 300ms 디바운스 (검색어 입력 시)

    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterStatus, filterMembership, viewMode, sortOrder]);

  // 페이지 변경 시 서버에 새 요청
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadMembers({
      page: newPage,
      limit: itemsPerPage,
      search: searchTerm || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      membershipType: filterMembership !== 'all' ? filterMembership : undefined,
      withdrawn: viewMode === 'withdrawn',
      sort: sortOrder,
    });
  };

  // 페이지당 항목 수 변경
  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
    loadMembers({
      page: 1,
      limit: newLimit,
      search: searchTerm || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      membershipType: filterMembership !== 'all' ? filterMembership : undefined,
      withdrawn: viewMode === 'withdrawn',
      sort: sortOrder,
    });
  };

  // 이용권 텍스트 변환 (화면 배지와 동일한 내용, 엑셀용 문자열)
  const getMembershipText = (m: Member): string => {
    const voucherName = (m as any).voucherName as string | undefined;
    switch (m.membershipType) {
      case 'hourly':
        return `${voucherName || '시간권'} (${m.remainingTime ?? 0}시간)`;
      case 'daily':
        return voucherName || '일일권';
      case 'monthly': {
        const name = voucherName || '정기권';
        // ⭐ 만료일을 마지막 사용일(자정경계 -1일) 기준으로 표기 — 상세 만료일과 통일
        const ymd = formatExpiryYMD(m.expiryDate);
        return ymd ? `${name} (~${ymd})` : name;
      }
      default:
        return '이용권 없음';
    }
  };

  // 엑셀 내보내기 (현재 화면 필터/검색/재원·퇴원 조건에 맞는 전체 회원)
  const [exporting, setExporting] = useState(false);
  const handleExportExcel = async () => {
    try {
      setExporting(true);

      // 현재 필터 조건 그대로, 페이지 제한 없이 전체 조회
      const response = await memberRepository.getMembers({
        page: 1,
        limit: 9999,
        search: searchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        membershipType: filterMembership !== 'all' ? filterMembership : undefined,
        withdrawn: viewMode === 'withdrawn',
      });
      const rows = response.members || [];

      if (rows.length === 0) {
        alert('내보낼 회원이 없습니다.');
        return;
      }

      // 화면에 보이는 컬럼 그대로 변환
      const excelData = rows.map((m) => ({
        이름: m.name,
        회원번호: m.user_number || m.member_id || m.memberNumber || '',
        구분: (m as any).member_category || '',
        이용권: getMembershipText(m),
        연락처: m.phone || '',
        좌석번호: m.assigned_seat
          ? (m.assigned_seat_code || `좌석 ${m.assigned_seat}`)
          : '',
        얼굴등록: m.face_registered ? '등록' : '미등록',
        '학부모 연락처': (m as any).parent_phone_1 || '',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 12 }, // 이름
        { wch: 12 }, // 회원번호
        { wch: 10 }, // 구분
        { wch: 22 }, // 이용권
        { wch: 16 }, // 연락처
        { wch: 12 }, // 좌석번호
        { wch: 10 }, // 얼굴등록
        { wch: 16 }, // 학부모 연락처
      ];

      const workbook = XLSX.utils.book_new();
      const sheetName = viewMode === 'withdrawn' ? '퇴원생' : '회원목록';
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      const today = getTodayKST();
      const filename = `${viewMode === 'withdrawn' ? '퇴원생' : '회원목록'}_${today}.xlsx`;
      XLSX.writeFile(workbook, filename);

      alert(`${rows.length.toLocaleString()}명의 회원 정보를 다운로드했습니다.`);
    } catch (error) {
      console.error('회원 엑셀 내보내기 실패:', error);
      alert('엑셀 내보내기에 실패했습니다.');
    } finally {
      setExporting(false);
    }
  };

  // 학부모 초대 모달 열기
  const openInviteModal = (student: Member) => {
    console.log('🔍 [DEBUG] 학생 데이터:', {
      id: student.id,
      name: student.name,
      phone: student.phone,
      parent_phone_1: student.parent_phone_1,
      parent_phone_2: student.parent_phone_2,
      hasParentPhone1: !!student.parent_phone_1,
      hasParentPhone2: !!student.parent_phone_2,
      전체데이터: student
    });
    setSelectedStudent(student);
    setInviteUrl('');
    setInviteError('');
    setCopied(false);
    setShowInviteModal(true);
  };

  // 학부모 초대 모달 닫기
  const closeInviteModal = () => {
    setShowInviteModal(false);
    setSelectedStudent(null);
    setInviteUrl('');
    setInviteError('');
    setCopied(false);
  };

  // 학부모 초대 링크 생성 및 발송
  const handleCreateInvite = async (student?: Member) => {
    const targetStudent = student ?? selectedStudent;

    if (!targetStudent) {
      setInviteError('학생 정보가 없습니다.');
      return;
    }

    setInviteLoading(true);
    setInviteError('');

    try {
      // 빈 객체로 전송: 백엔드가 자동으로 parent_phone_1 사용
      const response = await studentWithParentManagementRepository.createParentInvite(
        targetStudent.id,
        {}
      );

      // 백엔드에서 초대 URL도 같이 돌려주므로 그대로 저장
      setInviteUrl(response.inviteUrl);
      setInviteError('');
    } catch (error) {
      console.error('초대 링크 생성 실패:', error);
      setInviteError(
        error instanceof Error ? error.message : '초대 링크 생성에 실패했습니다.'
      );
    } finally {
      setInviteLoading(false);
    }
  };

  // 초대 링크 복사
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('복사 실패:', error);
      alert('복사에 실패했습니다');
    }
  };

  // 회원 추가 핸들러
  const handleAddMember = async () => {
    // 퇴원한 기존 회원이면 비교 창을 띄운다 — 항목별로 기존 유지/새 값을 고르고 복구 (2026-10-28)
    const success = await createMember(newMember, (conflict) => setRestoreConflict(conflict));
    if (success) {
      setShowAddModal(false);
      setNewMember({ name: '', phone: '', parent_phone_1: '', parent_phone_2: '' });
    }
  };

  // ── 사물함 (2026-09-02) ────────────────────────────────────
  // 좌석과 별개 기능이라 좌석 관련 함수는 건드리지 않는다.
  // 실패해도 회원 상세보기 자체는 열려야 하므로 오류를 삼키고 값만 비운다.
  const loadLocker = async (studentId: string) => {
    try {
      const res = await apiClient.get(`/v1/owner/lockers/by-student/${studentId}`);
      const d: any = res.data ?? res;
      setLockerNumber(d?.has_locker ? d.locker.locker_number : null);
    } catch {
      setLockerNumber(null);
    }
  };

  const handleAssignLocker = async () => {
    if (!selectedMember) return;
    const n = parseInt(newLockerNumber, 10);
    if (!n) { setLockerMsg('사물함 번호를 입력해 주세요.'); return; }
    try {
      const res = await apiClient.post(
        `/v1/owner/lockers/by-student/${selectedMember.id}/assign`,
        { locker_number: n }
      );
      const d: any = res.data ?? res;
      setLockerMsg(d?.message || '사물함을 배정했습니다.');
      setIsEditingLocker(false);
      setNewLockerNumber('');
      await loadLocker(selectedMember.id);
    } catch (e: any) {
      // 서버가 "12번은 김민수 학생이 쓰고 있습니다" 처럼 이유를 알려준다 — 그대로 보여준다
      setLockerMsg(e?.response?.data?.detail || '사물함을 배정하지 못했습니다.');
    }
  };

  const handleReleaseLocker = async () => {
    if (!selectedMember) return;
    try {
      await apiClient.post(`/v1/owner/lockers/by-student/${selectedMember.id}/release`);
      setLockerMsg('사물함을 비웠습니다.');
      setIsEditingLocker(false);
      setNewLockerNumber('');
      await loadLocker(selectedMember.id);
    } catch (e: any) {
      setLockerMsg(e?.response?.data?.detail || '비우지 못했습니다.');
    }
  };

  // 회원 상세보기 핸들러
  const handleViewMemberDetail = async (member: Member) => {
    const memberIdentifier = member.member_id || member.id;
    await loadMemberDetail(memberIdentifier);
    // 좌석 셀렉트에 빈좌석 + 본인 현재 좌석을 함께 보이도록 재로드
    loadAvailableSeats(member.id);
    loadLocker(member.id);   // 사물함은 좌석과 별개로 따로 조회한다
    setIsEditingLocker(false);
    setLockerMsg('');
    setShowDetailModal(true);
  };

  // 모달 닫기 핸들러
  const closeModal = () => {
    setShowDetailModal(false);
    setSelectedMember(null);
    setActiveTab('basic');
  };

  // 얼굴 삭제 핸들러
  const handleDeleteFaceClick = async (member: Member) => {
    await deleteMemberFace(member);
  };

  // 입실번호 수정 핸들러
  const handleUpdateCheckinCode = async (memberId: string, code: string) => {
    try {
      const response = await apiClient.patch(`/v1/owner/members/${memberId}/checkin-code`, {
        checkin_code: code,
      });
      alert(response.data.message || '입실번호가 변경되었습니다.');
      // 회원 목록 새로고침
      await loadMembers();
      if (selectedMember && selectedMember.id === memberId) {
        setSelectedMember({ ...selectedMember, checkin_code: code });
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || '입실번호 변경에 실패했습니다.');
    }
  };

  const handleUpdateName = async () => {
    if (!selectedMember || !newName.trim()) {
      alert('이름을 입력해주세요.');
      return;
    }

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/name`, {
        new_name: newName.trim(),
      });
      alert(response.data.message || '이름이 성공적으로 변경되었습니다.');
      setIsEditingName(false);
      setNewName('');
      // 회원 목록 새로고침
      await loadMembers();
      // 상세 정보 새로고침
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('이름 변경 오류:', error);
      alert(`이름 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  const handleUpdatePhone = async () => {
    if (!selectedMember || !newPhone) return;

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/phone`, {
        new_phone: newPhone,
      });
      alert(response.data.message + '\n' + response.data.notice);
      setIsEditingPhone(false);
      setNewPhone('');
      // 회원 목록 새로고침
      await loadMembers();
      // 상세 정보 새로고침
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('연락처 변경 오류:', error);
      alert(`연락처 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  const handleUpdateEmail = async () => {
    if (!selectedMember || !newEmail) return;

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      alert('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/email`, {
        new_email: newEmail,
      });
      alert(response.data.message || '이메일이 성공적으로 변경되었습니다.');
      setIsEditingEmail(false);
      setNewEmail('');
      // 회원 목록 새로고침
      await loadMembers();
      // 상세 정보 새로고침
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('이메일 변경 오류:', error);
      alert(`이메일 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학부모 1 이름 업데이트
  const handleUpdateParent1Name = async () => {
    if (!selectedMember) return;
    try {
      await apiClient.patch(`/v1/owner/members/${selectedMember.id}/parent-phones`, {
        parent1_name: newParent1Name,
      });
      (selectedMember as any).parent1_name = newParent1Name;
      setIsEditingParent1Name(false);
      alert('학부모 1 이름이 변경되었습니다.');
    } catch (error: any) {
      alert(`학부모 1 이름 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학부모 2 이름 업데이트
  const handleUpdateParent2Name = async () => {
    if (!selectedMember) return;
    try {
      await apiClient.patch(`/v1/owner/members/${selectedMember.id}/parent-phones`, {
        parent2_name: newParent2Name,
      });
      (selectedMember as any).parent2_name = newParent2Name;
      setIsEditingParent2Name(false);
      alert('학부모 2 이름이 변경되었습니다.');
    } catch (error: any) {
      alert(`학부모 2 이름 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학부모 연락처 저장 전 확인 (2026-10-28 대표 지시) — 기존과 다른 번호로 바꾸거나 지울 때 한 번 묻는다.
  //   형식이 틀린 번호는 확인 없이 그대로 보내 서버 400 안내를 보여 준다.
  const requestSaveParentPhone = (slot: 1 | 2) => {
    if (!selectedMember) return;
    const oldVal = (slot === 1 ? selectedMember.parent_phone_1 : selectedMember.parent_phone_2) || '';
    const newVal = (slot === 1 ? newParentPhone1 : newParentPhone2) || '';
    const digits = (v: string) => v.replace(/[^0-9]/g, '');
    const save = slot === 1 ? handleUpdateParentPhone1 : handleUpdateParentPhone2;
    if (digits(oldVal) === digits(newVal)) { save(); return; }        // 안 바뀜
    if (digits(newVal) && !isValidMobile(newVal)) { save(); return; } // 서버가 400 으로 안내
    if (!digits(oldVal) && !digits(newVal)) { save(); return; }
    setParentPhoneConfirm({ slot, oldVal, newVal });
  };

  const renderParentPhoneConfirm = (slot: 1 | 2) => {
    if (!parentPhoneConfirm || parentPhoneConfirm.slot !== slot) return null;
    const { oldVal, newVal } = parentPhoneConfirm;
    const cleared = !newVal.replace(/[^0-9]/g, '');
    const fmt = (v: string) => (v.replace(/[^0-9]/g, '') ? maskPhone(v) : '(없음)');
    return (
      <div className="mt-2 ml-6 p-3 rounded border border-amber-300 bg-amber-50 text-sm" data-testid={`parent-phone-confirm-${slot}`}>
        <p className="text-gray-800">
          학부모 연락처가 <b>{fmt(oldVal)}</b> → <b>{cleared ? '(없음)' : fmt(newVal)}</b> 로 바뀝니다.
        </p>
        <p className={cleared ? 'text-red-700 mt-1' : 'text-gray-700 mt-1'}>
          {cleared ? '이 번호로는 학부모 알림이 더 이상 가지 않습니다.' : '바뀐 번호로 입·퇴실 알림이 갑니다.'}
        </p>
        <div className="mt-2 flex gap-2">
          <button
            onClick={() => {
              setParentPhoneConfirm(null);
              (slot === 1 ? handleUpdateParentPhone1 : handleUpdateParentPhone2)();
            }}
            className={`px-3 py-1 text-white rounded text-xs ${cleared ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {cleared ? '지우기' : '바꾸기'}
          </button>
          <button
            onClick={() => setParentPhoneConfirm(null)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
          >
            취소
          </button>
        </div>
      </div>
    );
  };

  // 학부모 1 전화번호 업데이트
  const handleUpdateParentPhone1 = async () => {
    if (!selectedMember) return;

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/parent-phones`, {
        parent_phone_1: newParentPhone1 || null,
      });
      alert('학부모 1 연락처가 성공적으로 변경되었습니다.');
      setIsEditingParentPhone1(false);
      setNewParentPhone1('');
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('학부모 1 연락처 변경 오류:', error);
      alert(`학부모 1 연락처 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학부모 2 전화번호 업데이트
  const handleUpdateParentPhone2 = async () => {
    if (!selectedMember) return;

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/parent-phones`, {
        parent_phone_2: newParentPhone2 || null,
      });
      alert('학부모 2 연락처가 성공적으로 변경되었습니다.');
      setIsEditingParentPhone2(false);
      setNewParentPhone2('');
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('학부모 2 연락처 변경 오류:', error);
      alert(`학부모 2 연락처 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 좌석 배정 업데이트
  const handleUpdateSeat = async () => {
    if (!selectedMember) return;

    try {
      if (newSeatId) {
        // 좌석 배정
        const response = await apiClient.post(`/v1/owner/members/${selectedMember.id}/assign-seat`, {
          seat_id: newSeatId,
        });
        alert('좌석이 성공적으로 배정되었습니다.');
        setIsEditingSeat(false);
        setNewSeatId('');
        await loadMembers();
        if (selectedMember) {
          await loadMemberDetail(selectedMember.id);
        }
      } else {
        // 좌석 해제
        const response = await apiClient.post(`/v1/owner/members/${selectedMember.id}/release-seat`, {});
        alert('좌석 배정이 해제되었습니다.');
        setIsEditingSeat(false);
        setNewSeatId('');
        await loadMembers();
        if (selectedMember) {
          await loadMemberDetail(selectedMember.id);
        }
      }
    } catch (error: any) {
      console.error('좌석 배정 변경 오류:', error);
      alert(`좌석 배정 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학부모 1 알림 수신 여부 토글
  const handleToggleParent1Notification = async () => {
    if (!selectedMember) return;

    try {
      const newValue = !selectedMember.parent1_notification_enabled;
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/parent-notifications`, {
        parent1_notification_enabled: newValue,
      });
      alert(`학부모 1 알림이 ${newValue ? '활성화' : '비활성화'}되었습니다.`);
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('학부모 1 알림 설정 변경 오류:', error);
      alert(`학부모 1 알림 설정 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학부모 2 알림 수신 여부 토글
  const handleToggleParent2Notification = async () => {
    if (!selectedMember) return;

    try {
      const newValue = !selectedMember.parent2_notification_enabled;
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/parent-notifications`, {
        parent2_notification_enabled: newValue,
      });
      alert(`학부모 2 알림이 ${newValue ? '활성화' : '비활성화'}되었습니다.`);
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('학부모 2 알림 설정 변경 오류:', error);
      alert(`학부모 2 알림 설정 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 엑셀 템플릿 다운로드
  const handleDownloadTemplate = () => {
    // 서버에 저장된 엑셀 템플릿 파일 다운로드
    const link = document.createElement('a');
    link.href = '/templates/member_import_template.xlsx';
    link.download = '회원_일괄등록_템플릿.xlsx';
    link.click();
  };

  // 엑셀 파일 선택 핸들러
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // 엑셀 일괄 업로드 핸들러
  const handleBulkUpload = async () => {
    if (!selectedFile) {
      alert('파일을 선택해주세요.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await apiClient.post('/v1/owner/members/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setBulkUploadResult(response.data);
      setShowBulkUploadModal(false);
      setShowBulkResultModal(true);
      setSelectedFile(null);
      // 회원 목록 새로고침
      await loadMembers();
    } catch (error: any) {
      console.error('엑셀 업로드 오류:', error);
      alert(`업로드 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    } finally {
      setIsUploading(false);
    }
  };

  // 대표 학부모 변경
  const handleUpdatePrimaryParent = async (value: 1 | 2) => {
    if (!selectedMember) return;

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/primary-parent`, {
        primary_parent: value,
      });
      alert(`대표 학부모가 학부모 ${value}로 변경되었습니다.`);
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('대표 학부모 변경 오류:', error);
      alert(`대표 학부모 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 생년월일 업데이트 (basic2 API)
  const handleUpdateBirthDate = async () => {
    if (!selectedMember) return;
    try {
      await apiClient.patch(
        `/v1/owner/members-extended/${(selectedMember as any).user_id || (selectedMember as any).id}/basic2`,
        { birth_date: newBirthDate || null }
      );
      (selectedMember as any).birth_date = newBirthDate || null;
      setIsEditingBirthDate(false);
      alert('생년월일이 변경되었습니다.');
    } catch (error: any) {
      alert(`생년월일 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 회원 구분 업데이트 (basic2 API)
  const handleUpdateMemberCategory = async () => {
    if (!selectedMember) return;
    try {
      await apiClient.patch(
        `/v1/owner/members-extended/${(selectedMember as any).user_id || (selectedMember as any).id}/basic2`,
        { member_category: newMemberCategory || null }
      );
      (selectedMember as any).member_category = newMemberCategory || null;
      setIsEditingMemberCategory(false);
      alert('회원 구분이 변경되었습니다.');
    } catch (error: any) {
      alert(`회원 구분 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학교 업데이트
  const handleUpdateSchool = async () => {
    if (!selectedMember) return;

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/student-info`, {
        school: newSchool || null,
      });
      alert('학교가 변경되었습니다.');
      setIsEditingSchool(false);
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('학교 변경 오류:', error);
      alert(`학교 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 학년 업데이트
  const handleUpdateGrade = async () => {
    if (!selectedMember) return;

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/student-info`, {
        grade: newGrade || null,
      });
      alert('학년이 변경되었습니다.');
      setIsEditingGrade(false);
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('학년 변경 오류:', error);
      alert(`학년 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 반 업데이트
  const handleUpdateClassName = async () => {
    if (!selectedMember) return;
    try {
      await apiClient.patch(`/v1/owner/members/${selectedMember.id}/student-info`, {
        class_name: newClassName || null,
      });
      alert('반이 변경되었습니다.');
      setIsEditingClassName(false);
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('반 변경 오류:', error);
      alert(`반 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 성별 업데이트
  const handleUpdateGender = async () => {
    if (!selectedMember) return;

    try {
      const response = await apiClient.patch(`/v1/owner/members/${selectedMember.id}/student-info`, {
        gender: newGender || null,
      });
      alert('성별이 변경되었습니다.');
      setIsEditingGender(false);
      await loadMembers();
      if (selectedMember) {
        await loadMemberDetail(selectedMember.id);
      }
    } catch (error: any) {
      console.error('성별 변경 오류:', error);
      alert(`성별 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
    }
  };

  // 회원 삭제 핸들러
  const handleDeleteMember = async () => {
    if (!selectedMember) return;

    const confirmed = window.confirm(
      `${selectedMember.name} 회원을 퇴원 처리하시겠습니까?\n\n` +
      `· 출결·이용권·결제 기록은 보존됩니다.\n` +
      `· 얼굴 등록은 삭제됩니다 — '재원 복귀' 해도 되살아나지 않으므로 다시 등록해야 합니다.\n` +
      `· 입실번호는 반납되며, 복귀 시 새 번호가 발급됩니다.`
    );

    if (!confirmed) return;

    try {
      await DashboardService.deleteMember(selectedMember.id);
      alert('퇴원 처리되었습니다. (퇴원생 목록에서 확인 가능)');
      setShowDetailModal(false);
      setSelectedMember(null);
      // 회원 목록 새로고침
      await loadMembers({ page: 1, limit: itemsPerPage, withdrawn: viewMode === 'withdrawn' });
    } catch (error) {
      console.error('퇴원 처리 오류:', error);
      alert('퇴원 처리에 실패했습니다.');
    }
  };

  // 퇴원생 → 재원 복귀
  const handleRestoreMember = async (member: Member) => {
    const confirmed = window.confirm(`${member.name} 회원을 재원 복귀시키겠습니까?\n\n다시 재원생 목록으로 돌아갑니다.`);
    if (!confirmed) return;
    try {
      await apiClient.post(`/v1/owner/members/${member.id}/restore`, {});
      alert('재원 복귀되었습니다.');
      await loadMembers({ page: 1, limit: itemsPerPage, withdrawn: viewMode === 'withdrawn' });
    } catch (error: any) {
      console.error('재원 복귀 오류:', error);
      alert(error?.response?.data?.detail || '재원 복귀에 실패했습니다.');
    }
  };

  return (
    <div className="p-6">
      {/* 퇴원 회원 복구 전 비교 창 (2026-10-28) */}
      {restoreConflict && (
        <RestoreCompareModal
          conflict={restoreConflict}
          onCancel={() => setRestoreConflict(null)}
          onRestored={async () => {
            setRestoreConflict(null);
            alert('회원이 복구되었습니다.');
            setShowAddModal(false);
            setNewMember({ name: '', phone: '', parent_phone_1: '', parent_phone_2: '' });
            await loadMembers({ page: 1, limit: itemsPerPage, withdrawn: viewMode === 'withdrawn' });
          }}
        />
      )}
      {/* 헤더 — 입장 차단 상태는 제목 줄 오른쪽 작은 배지로 (2026-09-10 대표 지시)
          종전엔 가로 전체를 차지하는 띠라 태블릿에서 목록이 그만큼 아래로 밀렸다. */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">회원 관리</h1>
          <p className="text-gray-600 mt-1">회원 정보 조회, 이용권 관리, 출입 기록을 관리합니다</p>
        </div>
        {blockExpiredEntry !== null && (
          <a
            href="/owner/integrations"
            title={
              blockExpiredEntry
                ? '입장 차단 켜짐 — 이용권이 만료된 회원은 들어올 수 없습니다. 눌러서 설정 변경'
                : '입장 차단 꺼짐 — 이용권이 만료된 회원도 들어올 수 있습니다. 눌러서 설정 변경'
            }
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-medium transition-colors ${
              blockExpiredEntry
                ? 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100'
                : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
            }`}
          >
            {blockExpiredEntry ? (
              <NoSymbolIcon className="h-4 w-4" />
            ) : (
              <InformationCircleIcon className="h-4 w-4" />
            )}
            <span className="hidden sm:inline">입장 차단</span>
            <span className={blockExpiredEntry ? 'font-semibold' : ''}>
              {blockExpiredEntry ? '켜짐' : '꺼짐'}
            </span>
          </a>
        )}
      </div>

      {/* 재원생 / 퇴원생 전환 탭 */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {([['active', '재원생'], ['withdrawn', '📦 퇴원생']] as const).map(([mode, label]) => (
          <button
            key={mode}
            onClick={() => { setViewMode(mode); setSearchTerm(''); setFilterStatus('all'); }}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              viewMode === mode
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* 태블릿으로 직접 등록 신청한 학생 — 대기자가 없으면 아무것도 안 보인다.
          🔴 별도 메뉴로 빼면 원장이 들어가 보지 않아 접수가 묻힌다. 늘 보는 화면 맨 위에 둔다. */}
      <SelfRegistrationPending onChanged={loadMembers} />

      {/* 상단 툴바 */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* 🔴 검색과 필터를 한 줄에 욱여넣으면 필터가 공간을 다 먹어 검색칸이
                돋보기만 남을 만큼 쪼그라든다(태블릿에서 실제로 그랬다).
                최소 너비를 정해 두고, 자리가 모자라면 필터가 아랫줄로 내려가게 한다. */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 flex-1">
            {/* 검색 */}
            <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[260px] max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="이름, 전화번호, 회원번호로 검색"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 필터 — 🔴 좁은 화면에서 세 개를 한 줄에 두면 마지막(정렬)이
                   화면 밖으로 잘려 아예 못 쓴다. 넘치면 아랫줄로 흐르게 한다. */}
            <div className="flex flex-wrap gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | MemberStatus)}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              >
                <option value="all">전체 상태</option>
                <option value="active">정상</option>
                <option value="expired">만료</option>
                <option value="waiting">대기</option>
                <option value="inactive">퇴원</option>
                <option value="suspended">정지</option>
              </select>

              <select
                value={filterMembership}
                onChange={(e) => setFilterMembership(e.target.value as 'all' | MembershipType)}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              >
                <option value="all">전체 이용권</option>
                <option value="hourly">시간권</option>
                <option value="daily">일일권</option>
                <option value="monthly">정액권</option>
                <option value="none">없음</option>
              </select>

              {/* 정렬 — 고른 순서는 이 PC 에 기억된다 */}
              <select
                value={sortOrder}
                onChange={(e) => changeSortOrder(e.target.value as MemberSortOrder)}
                className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                title="정렬 순서 (이 PC 에 기억됩니다)"
              >
                {MEMBER_SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <button
                type="button"
                onClick={saveSortAsStoreDefault}
                disabled={savingSortDefault}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
                title="다른 PC 에서 처음 열 때 이 순서로 보이게 합니다"
              >
                {savingSortDefault ? '저장 중...' : '매장 기본으로'}
              </button>
            </div>
          </div>

          {sortDefaultMsg && (
            <div className="text-sm text-gray-500 whitespace-nowrap">{sortDefaultMsg}</div>
          )}

          {/* 액션 버튼 */}
          <div className="flex gap-2">
            <button
              onClick={() => { loadAvailableSeats(); setShowAddModal(true); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <UserPlusIcon className="h-5 w-5" />
              회원 추가
            </button>
            <button
              onClick={() => setShowBulkUploadModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              엑셀 일괄 등록
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {exporting ? '내보내는 중...' : '내보내기'}
            </button>
          </div>
        </div>
      </div>

      {/* 로딩 상태 */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">회원 정보를 불러오는 중...</p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && !loading && (
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-900 font-medium mb-2">데이터 로드 실패</p>
            <p className="text-gray-500 text-sm mb-4">{error}</p>
            <button
              onClick={() => loadMembers({ page: 1, limit: itemsPerPage })}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              다시 시도
            </button>
          </div>
        </div>
      )}

      {/* 회원 목록 */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {paginatedMembers.length === 0 ? (
            <div className="p-8 text-center">
              <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {totalMembers === 0
                  ? (viewMode === 'withdrawn' ? '퇴원생이 없습니다' : '등록된 회원이 없습니다')
                  : '검색 결과가 없습니다'}
              </p>
              {totalMembers === 0 && viewMode === 'active' && (
                <button
                  onClick={() => { loadAvailableSeats(); setShowAddModal(true); }}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <UserPlusIcon className="h-5 w-5" />
                  첫 회원 등록하기
                </button>
              )}
            </div>
          ) : (
            <div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">이름</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">구분</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">이용권</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">연락처</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">좌석번호</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">얼굴등록</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">학부모 연락처</th>
                    {viewMode === 'withdrawn' && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">관리</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewMemberDetail(member)}>
                      {/* 이름 */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900">{member.name}</div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {member.user_number || member.member_id || member.memberNumber || 'N/A'}
                          </span>
                        </div>
                      </td>
                      {/* 상태 (정상/만료/대기/퇴원 등) */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getStatusBadge(member.status)}
                      </td>
                      {/* 구분 (성인/학생/N수생 등) */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {(member as any).member_category ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {(member as any).member_category}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      {/* 이용권 */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {getMembershipBadge(
                          member.membershipType,
                          member.remainingTime,
                          member.expiryDate,
                          member.voucherName
                        )}
                      </td>
                      {/* 연락처 */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{member.phone || '-'}</span>
                      </td>
                      {/* 좌석번호 */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {member.assigned_seat ? (
                          <span className="text-sm font-medium text-purple-600">
                            {member.assigned_seat_code || `좌석 ${member.assigned_seat}`}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      {/* 얼굴등록 */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {member.face_registered ? (
                          <div className="flex items-center gap-1">
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                            <span className="text-sm text-green-700">등록</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <XCircleIcon className="h-5 w-5 text-gray-400" />
                            <span className="text-sm text-gray-500">미등록</span>
                          </div>
                        )}
                      </td>
                      {/* 학부모 연락처 */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-700">{(member as any).parent_phone_1 || '-'}</span>
                        {/* 학부모 번호 상태는 서버가 판정해 내려준다 (2026-10-28) — 틀린 번호는 입·퇴실 알림이 안 간다 */}
                        {(member as any).parent_phone_issue === 'invalid' && (
                          <span
                            className="ml-2 inline-block px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700"
                            title="학부모 번호가 잘못 저장되어 입·퇴실 알림이 가지 않습니다. 회원을 눌러 학부모 연락처를 고쳐 주세요."
                          >
                            학부모 번호 확인 필요
                          </span>
                        )}
                        {(member as any).parent_phone_issue === 'empty' && (member.status || 'active') === 'active' && (
                          <span
                            className="ml-2 inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-500"
                            title="학부모 번호가 없어 입·퇴실 알림이 가지 않습니다."
                          >
                            학부모 번호 없음
                          </span>
                        )}
                      </td>
                      {/* 재원 복귀 (퇴원생 목록에서만) */}
                      {viewMode === 'withdrawn' && (
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRestoreMember(member); }}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm inline-flex items-center gap-1"
                          >
                            <ArrowPathIcon className="h-4 w-4" /> 재원 복귀
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>

              {/* 페이지네이션 */}
              {totalMembers > 0 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  {/* 페이지당 항목 수 선택 */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">페이지당</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                    >
                      <option value={20}>20개</option>
                      <option value={50}>50개</option>
                      <option value={100}>100개</option>
                      <option value={9999}>전체</option>
                    </select>
                    <span className="text-sm text-gray-700">
                      (총 {totalMembers}명 중 {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalMembers)}명)
                    </span>
                  </div>

                  {/* 페이지 번호 */}
                  {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      이전
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className={`px-3 py-1 border rounded-md text-sm ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'border-gray-300 hover:bg-gray-100'
                            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages || loading}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      다음
                    </button>
                  </div>
                  )}
                </div>
              </div>
            )}
            </div>
          )}
        </div>
      )}

      {/* 회원 추가 모달 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">새 회원 등록</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">이름 *</label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="홍길동"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  학생 본인 전화번호 (게이트 출입용) *
                </label>
                <input
                  type="tel"
                  value={newMember.phone}
                  onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="010-1234-5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  학부모 연락처 1 (알림 수신용)
                </label>
                <input
                  type="tel"
                  value={newMember.parent_phone_1 || ''}
                  onChange={(e) => setNewMember({ ...newMember, parent_phone_1: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="010-9876-5432 (선택)"
                />
              </div>

              {/* 회의 2026-07-04: 추가폼은 기본만(이름·학생폰·학부모폰1·생년월일·구분).
                  학부모연락처2·학교·학년·반·성별은 상세보기에서 추가하는 방향으로 제거. */}

              {/* 생년월일·회원구분 — 상세로 또 들어가는 "두 번 클릭" 제거 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">생년월일 (선택)</label>
                <input
                  type="date"
                  value={newMember.birth_date || ''}
                  onChange={(e) => setNewMember({ ...newMember, birth_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">회원구분 (선택)</label>
                <select
                  value={newMember.member_category || ''}
                  onChange={(e) => setNewMember({ ...newMember, member_category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">선택안함</option>
                  {['고등학생', '중학생', '초등학생', 'N수생', '성인 수험생', '전문직', '기타'].map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* 좌석 (선택) — 빈 좌석만 표시. 선택 시 등록과 동시에 배정됨 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">좌석 (선택 · 빈 좌석만)</label>
                <select
                  value={newMember.seat_id || ''}
                  onChange={(e) => setNewMember({ ...newMember, seat_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">좌석 미배정</option>
                  {/* value 는 상세보기와 동일하게 seat_number (백엔드가 org 안에서 번호 우선 매칭) */}
                  {[...availableSeats]
                    .sort((a, b) => a.seat_number - b.seat_number)
                    .map((seat) => (
                      <option key={seat.id} value={String(seat.seat_number)}>
                        좌석 {seat.seat_code || seat.seat_number}
                      </option>
                    ))}
                </select>
                {availableSeats.length === 0 && (
                  <p className="mt-1 text-xs text-gray-400">배정 가능한 빈 좌석이 없습니다.</p>
                )}
              </div>
            </div>

            {/* 안내 문구 */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                ℹ️ <strong>게이트 출입은 학생 본인 전화번호(뒤 8자리)로만 가능합니다.</strong>
                <br />
                ℹ️ 학부모 번호는 알림 수신용으로만 사용되며, 출입 인증에는 사용되지 않습니다.
              </p>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleAddMember}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                등록
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewMember({
                    name: '',
                    phone: '',
                    parent_phone_1: '',
                    parent_phone_2: '',
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 엑셀 일괄 등록 모달 */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">회원 일괄 등록</h2>

            <div className="space-y-4">
              {/* 안내 문구 */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700 mb-2">
                  📋 <strong>업로드 방법</strong>
                </p>
                <ul className="text-xs text-blue-600 space-y-1 ml-4 list-disc">
                  <li>템플릿을 다운로드하여 회원 정보를 입력해주세요</li>
                  <li>Excel (.xlsx) 또는 CSV (.csv) 파일을 업로드할 수 있습니다</li>
                  <li>필수 항목: 학생 이름, 학생 전화번호</li>
                  <li>선택 항목: 이메일, 이용권, 학부모 연락처, 메모</li>
                </ul>
              </div>

              {/* 템플릿 다운로드 */}
              <div>
                <button
                  onClick={handleDownloadTemplate}
                  className="w-full px-4 py-3 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowDownTrayIcon className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-700">템플릿 다운로드</span>
                </button>
              </div>

              {/* 컬럼 안내 */}
              <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-800">
                <p className="font-medium mb-1">엑셀 컬럼 안내</p>
                <p>필수: <strong>student_name</strong>(이름), <strong>student_phone</strong>(전화번호)</p>
                <p>선택: membership_status(구분 재원/퇴원), school(학교), grade(학년), class_name(반), gender(성별 남/여), birth_date(생년월일), seat_number(좌석번호), parent_phone_1, parent_phone_2, email</p>
                <p className="mt-1">구분을 <strong>퇴원</strong>으로 적으면 재원생이 아니라 퇴원생 목록으로 들어갑니다. 비워두면 재원생입니다.</p>
              </div>

              {/* 파일 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  파일 선택 *
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                {selectedFile && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ 선택된 파일: {selectedFile.name}
                  </p>
                )}
              </div>

              {/* 업로드 진행 상태 */}
              {isUploading && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                    <span className="text-sm text-gray-600">업로드 중...</span>
                  </div>
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleBulkUpload}
                disabled={!selectedFile || isUploading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isUploading ? '업로드 중...' : '업로드'}
              </button>
              <button
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setSelectedFile(null);
                }}
                disabled={isUploading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 업로드 결과 모달 */}
      {showBulkResultModal && bulkUploadResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">업로드 결과</h2>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {/* 통계 */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">전체</p>
                  <p className="text-2xl font-bold text-blue-700">{bulkUploadResult.total_rows}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">신규 등록</p>
                  <p className="text-2xl font-bold text-green-700">{bulkUploadResult.created_count || 0}</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-600 mb-1">업데이트</p>
                  <p className="text-2xl font-bold text-yellow-700">{bulkUploadResult.updated_count || 0}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600 mb-1">실패</p>
                  <p className="text-2xl font-bold text-red-700">{bulkUploadResult.failed}</p>
                </div>
              </div>

              {/* 신규 등록 메시지 */}
              {(bulkUploadResult.created_count || 0) > 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <CheckCircleIcon className="h-5 w-5 inline mr-2" />
                    {bulkUploadResult.created_count}명의 신규 회원이 등록되었습니다.
                  </p>
                </div>
              )}

              {/* 업데이트된 회원 목록 */}
              {bulkUploadResult.updated_members && bulkUploadResult.updated_members.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ArrowPathIcon className="h-5 w-5 text-yellow-500" />
                    기존 회원 정보 업데이트 ({bulkUploadResult.updated_members.length}명)
                  </h3>
                  <div className="border border-yellow-200 rounded-lg overflow-hidden bg-yellow-50">
                    <table className="min-w-full divide-y divide-yellow-200">
                      <thead className="bg-yellow-100">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">
                            행
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">
                            이름
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">
                            전화번호
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-yellow-700 uppercase tracking-wider">
                            상태
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-yellow-100">
                        {bulkUploadResult.updated_members.map((member, index) => (
                          <tr key={index} className="hover:bg-yellow-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {member.row}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {member.name}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {member.phone}
                            </td>
                            <td className="px-4 py-3 text-sm text-yellow-600">
                              {member.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 에러 목록 */}
              {bulkUploadResult.errors.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                    오류 내역 ({bulkUploadResult.errors.length}건)
                  </h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            행 번호
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            필드
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            오류 메시지
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {bulkUploadResult.errors.map((error, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {error.row}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                              {error.field}
                            </td>
                            <td className="px-4 py-3 text-sm text-red-600">
                              {error.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 에러가 없는 경우 */}
              {bulkUploadResult.errors.length === 0 && bulkUploadResult.success > 0 && (
                <div className="text-center py-8">
                  <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-900">
                    모든 회원이 성공적으로 등록되었습니다!
                  </p>
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowBulkResultModal(false);
                  setBulkUploadResult(null);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 회원 상세 모달 */}
      {showDetailModal && selectedMember && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            // 배경 클릭 시에만 닫기 (모달 내부 클릭은 무시)
            if (e.target === e.currentTarget) {
              setShowDetailModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">회원 상세 정보</h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-500 p-1"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* 탭 메뉴 */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px overflow-x-auto">
                {(['basic', 'basic2', 'parent', 'attendance', 'ticket', 'schedule', 'patrol', 'penalty', 'exception', 'study', 'communication', 'memo'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-3 px-6 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab === 'basic' && '기본정보'}
                    {tab === 'basic2' && '기본정보 2'}
                    {tab === 'parent' && '학부모'}
                    {tab === 'attendance' && '출결'}
                    {tab === 'ticket' && '이용권'}
                    {tab === 'schedule' && '스케줄'}
                    {tab === 'patrol' && '순찰기록'}
                    {tab === 'penalty' && '벌점'}
                    {tab === 'exception' && '사유서'}
                    {tab === 'study' && '학습관리'}
                    {tab === 'communication' && '학부모소통'}
                    {tab === 'memo' && '메모'}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {/* 기본정보 탭 */}
              {activeTab === 'basic' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">기본 정보</h3>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center text-sm">
                          <IdentificationIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">회원번호:</span>
                          <span className="text-gray-900">
                            {selectedMember.user_number || selectedMember.member_id || selectedMember.memberNumber || 'N/A'}
                          </span>
                        </div>
                        {selectedMember.user_id && (
                          <div className="flex items-center text-sm">
                            <IdentificationIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-gray-500 mr-2">시스템 ID:</span>
                            <span className="text-xs text-gray-600">{selectedMember.user_id}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm">
                          <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">이름:</span>
                          {isEditingName ? (
                            <span className="flex items-center gap-2">
                              <input
                                type="text"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="이름 입력"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={handleUpdateName}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              >
                                저장
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingName(false);
                                  setNewName('');
                                }}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                              >
                                취소
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className="text-gray-900">{selectedMember.name}</span>
                              <button
                                onClick={() => {
                                  setIsEditingName(true);
                                  setNewName(selectedMember.name || '');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm">
                          <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">학생 본인 번호:</span>
                          {isEditingPhone ? (
                            <span className="flex items-center gap-2">
                              <input
                                type="text"
                                value={newPhone}
                                onChange={(e) => setNewPhone(e.target.value)}
                                placeholder="01012345678"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={handleUpdatePhone}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              >
                                저장
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingPhone(false);
                                  setNewPhone('');
                                }}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                              >
                                취소
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className="text-gray-900 font-medium">{selectedMember.phone || '미등록'}</span>
                              <button
                                onClick={() => {
                                  setIsEditingPhone(true);
                                  setNewPhone(selectedMember.phone || '');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                              <button
                                onClick={async () => {
                                  const phone = (selectedMember.phone || '').replace(/[-\s]/g, '');
                                  if (phone.length < 4) {
                                    alert('학생 전화번호가 등록되어 있지 않습니다.');
                                    return;
                                  }
                                  if (!window.confirm(`학생 계정 비밀번호를 ${phone.slice(-4)} 로 (재)설정하시겠습니까?\n• 신규: 첫 비번 발급\n• 번호 변경: 새 번호 뒷 4자리로 갱신\n• 분실: 복구\n(로그인 후 바로 바꾸지 않아도 됩니다. 원하면 본인이 나중에 바꿀 수 있습니다)`)) return;
                                  try {
                                    const r = await apiClient.post(`/v1/owner/members/${selectedMember.id}/reset-password`);
                                    alert(`✅ ${r.data.message}\n새 비밀번호: ${r.data.new_password}`);
                                  } catch (e: any) {
                                    alert(`비번 복구 실패: ${e.response?.data?.detail || '오류'}`);
                                  }
                                }}
                                className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs hover:bg-amber-200"
                                title="학생 계정 비밀번호를 전화번호 뒷 4자리로 (재)설정"
                              >
                                🔑 계정 생성/복구
                              </button>
                            </span>
                          )}
                        </div>
                        {/* 회원 상태 */}
                        <div className="flex items-center text-sm">
                          <CheckCircleIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">상태:</span>
                          {isEditingStatus ? (
                            <span className="flex items-center gap-2">
                              <select
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="active">정상</option>
                                <option value="expired">만료</option>
                                <option value="waiting">대기</option>
                                <option value="inactive">퇴원</option>
                                <option value="suspended">정지</option>
                              </select>
                              <button
                                onClick={async () => {
                                  try {
                                    await apiClient.patch(`/v1/owner/members/${selectedMember.id}/status`, { status: newStatus });
                                    alert('상태가 변경되었습니다.');
                                    setIsEditingStatus(false);
                                    await loadMembers();
                                    if (selectedMember) {
                                      await loadMemberDetail(selectedMember.id);
                                    }
                                  } catch (error: any) {
                                    console.error('상태 변경 오류:', error);
                                    alert(`상태 변경 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
                                  }
                                }}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              >
                                저장
                              </button>
                              <button
                                onClick={() => setIsEditingStatus(false)}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                              >
                                취소
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              {getStatusBadge(selectedMember.status)}
                              <button
                                onClick={() => {
                                  setIsEditingStatus(true);
                                  setNewStatus(selectedMember.status || 'active');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm">
                          <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span className="text-gray-500 mr-2 whitespace-nowrap">이메일:</span>
                          {isEditingEmail ? (
                            <span className="flex items-center gap-2">
                              <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                placeholder="example@email.com"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={handleUpdateEmail}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              >
                                저장
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingEmail(false);
                                  setNewEmail('');
                                }}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                              >
                                취소
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className="text-gray-900">{selectedMember.email || '미등록'}</span>
                              <button
                                onClick={() => {
                                  setIsEditingEmail(true);
                                  setNewEmail(selectedMember.email || '');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                            </span>
                          )}
                        </div>

                        {/* 학생 정보 섹션 (2열 그리드 - 공간 효율) */}
                        <div className="pt-4 mt-4 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            🎓 학생 정보
                          </h4>
                          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                            {/* 학교 */}
                            <div className="flex items-center gap-2 text-sm group min-h-[32px]">
                              <AcademicCapIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 whitespace-nowrap flex-shrink-0">학교:</span>
                              {isEditingSchool ? (
                                <>
                                  <input
                                    type="text"
                                    value={newSchool}
                                    onChange={(e) => setNewSchool(e.target.value)}
                                    placeholder="학교명"
                                    className="px-2 py-1 border border-gray-300 rounded text-sm flex-1 min-w-0"
                                  />
                                  <button onClick={handleUpdateSchool} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex-shrink-0">저장</button>
                                  <button onClick={() => { setIsEditingSchool(false); setNewSchool(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 flex-shrink-0">취소</button>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-900 flex-1 min-w-0 truncate">{selectedMember.school || <span className="text-gray-400">미등록</span>}</span>
                                  <button
                                    onClick={() => { setIsEditingSchool(true); setNewSchool(selectedMember.school || ''); }}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 flex-shrink-0"
                                  >
                                    <PencilIcon className="h-3 w-3 inline" /> 변경
                                  </button>
                                </>
                              )}
                            </div>

                            {/* 학년 */}
                            <div className="flex items-center gap-2 text-sm group min-h-[32px]">
                              <AcademicCapIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 whitespace-nowrap flex-shrink-0">학년:</span>
                              {isEditingGrade ? (
                                <>
                                  <select value={newGrade} onChange={(e) => setNewGrade(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm flex-1 min-w-0">
                                    <option value="">선택</option>
                                    {['초1','초2','초3','초4','초5','초6','중1','중2','중3','고1','고2','고3','재수생','기타'].map(g => <option key={g} value={g}>{g}</option>)}
                                  </select>
                                  <button onClick={handleUpdateGrade} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex-shrink-0">저장</button>
                                  <button onClick={() => { setIsEditingGrade(false); setNewGrade(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 flex-shrink-0">취소</button>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-900 flex-1 min-w-0">{selectedMember.grade || <span className="text-gray-400">미선택</span>}</span>
                                  <button
                                    onClick={() => { setIsEditingGrade(true); setNewGrade(selectedMember.grade || ''); }}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 flex-shrink-0"
                                  >
                                    <PencilIcon className="h-3 w-3 inline" /> 변경
                                  </button>
                                </>
                              )}
                            </div>

                            {/* 반 */}
                            <div className="flex items-center gap-2 text-sm group min-h-[32px]">
                              <UserGroupIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 whitespace-nowrap flex-shrink-0">반:</span>
                              {isEditingClassName ? (
                                <>
                                  <select value={newClassName} onChange={(e) => setNewClassName(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm flex-1 min-w-0">
                                    <option value="">선택</option>
                                    {Array.from({ length: 15 }, (_, i) => i + 1).map((n) => <option key={n} value={String(n)}>{n}반</option>)}
                                  </select>
                                  <button onClick={handleUpdateClassName} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex-shrink-0">저장</button>
                                  <button onClick={() => { setIsEditingClassName(false); setNewClassName(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 flex-shrink-0">취소</button>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-900 flex-1 min-w-0">{selectedMember.class_name || <span className="text-gray-400">미선택</span>}</span>
                                  <button
                                    onClick={() => { setIsEditingClassName(true); setNewClassName(selectedMember.class_name || ''); }}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 flex-shrink-0"
                                  >
                                    <PencilIcon className="h-3 w-3 inline" /> 변경
                                  </button>
                                </>
                              )}
                            </div>

                            {/* 성별 */}
                            <div className="flex items-center gap-2 text-sm group min-h-[32px]">
                              <UserIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 whitespace-nowrap flex-shrink-0">성별:</span>
                              {isEditingGender ? (
                                <>
                                  <select value={newGender} onChange={(e) => setNewGender(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm flex-1 min-w-0">
                                    <option value="">선택</option>
                                    <option value="male">남</option>
                                    <option value="female">여</option>
                                    <option value="other">기타</option>
                                  </select>
                                  <button onClick={handleUpdateGender} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex-shrink-0">저장</button>
                                  <button onClick={() => { setIsEditingGender(false); setNewGender(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 flex-shrink-0">취소</button>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-900 flex-1 min-w-0">{selectedMember.gender === 'male' ? '남' : selectedMember.gender === 'female' ? '여' : selectedMember.gender === 'other' ? '기타' : <span className="text-gray-400">미선택</span>}</span>
                                  <button
                                    onClick={() => { setIsEditingGender(true); setNewGender(selectedMember.gender || ''); }}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 flex-shrink-0"
                                  >
                                    <PencilIcon className="h-3 w-3 inline" /> 변경
                                  </button>
                                </>
                              )}
                            </div>

                            {/* 생년월일 */}
                            <div className="flex items-center gap-2 text-sm group min-h-[32px]">
                              <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 whitespace-nowrap flex-shrink-0">생년월일:</span>
                              {isEditingBirthDate ? (
                                <>
                                  <input type="date" value={newBirthDate} onChange={(e) => setNewBirthDate(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm flex-1 min-w-0" />
                                  <button onClick={handleUpdateBirthDate} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex-shrink-0">저장</button>
                                  <button onClick={() => { setIsEditingBirthDate(false); setNewBirthDate(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 flex-shrink-0">취소</button>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-900 flex-1 min-w-0">{(selectedMember as any).birth_date || <span className="text-gray-400">미등록</span>}</span>
                                  <button
                                    onClick={() => { setIsEditingBirthDate(true); setNewBirthDate((selectedMember as any).birth_date || ''); }}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 flex-shrink-0"
                                  >
                                    <PencilIcon className="h-3 w-3 inline" /> 변경
                                  </button>
                                </>
                              )}
                            </div>

                            {/* 회원 구분 */}
                            <div className="flex items-center gap-2 text-sm group min-h-[32px]">
                              <TagIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 whitespace-nowrap flex-shrink-0">회원구분:</span>
                              {isEditingMemberCategory ? (
                                <>
                                  <select value={newMemberCategory} onChange={(e) => setNewMemberCategory(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm flex-1 min-w-0">
                                    <option value="">선택</option>
                                    {['고등학생','중학생','초등학생','N수생','성인 수험생','전문직','기타'].map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                  <button onClick={handleUpdateMemberCategory} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 flex-shrink-0">저장</button>
                                  <button onClick={() => { setIsEditingMemberCategory(false); setNewMemberCategory(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 flex-shrink-0">취소</button>
                                </>
                              ) : (
                                <>
                                  <span className="text-gray-900 flex-1 min-w-0">{(selectedMember as any).member_category || <span className="text-gray-400">미선택</span>}</span>
                                  <button
                                    onClick={() => { setIsEditingMemberCategory(true); setNewMemberCategory((selectedMember as any).member_category || ''); }}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 flex-shrink-0"
                                  >
                                    <PencilIcon className="h-3 w-3 inline" /> 변경
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* 배정 좌석 */}
                          <div className="flex items-center text-sm mb-3">
                            <span className="text-gray-500 mr-2 w-16">배정 좌석:</span>
                            {isEditingSeat ? (
                              <span className="flex items-center gap-2 flex-1">
                                <select
                                  value={newSeatId}
                                  onChange={(e) => setNewSeatId(e.target.value)}
                                  className="px-2 py-1 border border-gray-300 rounded text-sm flex-1"
                                >
                                  <option value="">좌석 없음</option>
                                  {[...availableSeats]
                                    .sort((a, b) => a.seat_number - b.seat_number)
                                    .map((seat) => (
                                    // ⚠️ value 는 seat.seat_number 사용 — 백엔드가 같은 조직 안에서
                                    // seat_number 우선 매칭하기 때문. PK 를 보내면 다른 좌석의
                                    // seat_number 와 충돌해서 엉뚱한 좌석에 배정됨 (이루는자리: PK 400-821
                                    // / seat_number 1-449, 50개 겹침).
                                    <option key={seat.id} value={String(seat.seat_number)}>
                                      좌석 {seat.seat_code || seat.seat_number}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={handleUpdateSeat}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                >
                                  저장
                                </button>
                                <button
                                  onClick={() => {
                                    setIsEditingSeat(false);
                                    setNewSeatId('');
                                  }}
                                  className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                                >
                                  취소
                                </button>
                                {selectedMember.assigned_seat && (
                                  <button
                                    onClick={async () => {
                                      if (!window.confirm('좌석 배정을 삭제하시겠습니까?')) return;
                                      try {
                                        await apiClient.post(`/v1/owner/members/${selectedMember.id}/release-seat`);
                                        alert('좌석 배정이 삭제되었습니다.');
                                        setIsEditingSeat(false);
                                        setNewSeatId('');
                                        await loadMembers();
                                        if (selectedMember) {
                                          await loadMemberDetail(selectedMember.id);
                                        }
                                      } catch (error: any) {
                                        console.error('좌석 삭제 오류:', error);
                                        alert(`좌석 삭제 실패: ${error.response?.data?.detail || '알 수 없는 오류'}`);
                                      }
                                    }}
                                    className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                  >
                                    삭제
                                  </button>
                                )}
                              </span>
                            ) : (
                              <span className="flex items-center gap-2 flex-1">
                                <span className="text-gray-900">
                                  {selectedMember.assigned_seat ? `좌석 ${selectedMember.assigned_seat}` : '배정 안 됨'}
                                </span>
                                <button
                                  onClick={() => {
                                    setIsEditingSeat(true);
                                    // ⚠️ 드롭다운 value 가 seat_number 이므로 초기값도 seat_number 사용
                                    setNewSeatId(selectedMember.assigned_seat?.toString() || '');
                                  }}
                                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                                >
                                  <PencilIcon className="h-3 w-3 inline" /> 변경
                                </button>
                              </span>
                            )}
                          </div>

                          {/* 사물함 (2026-09-02) — 좌석과 별개. 여기서 줘도 좌석은 그대로 유지된다 */}
                          <div className="flex items-center text-sm mb-1">
                            <span className="text-gray-500 mr-2 w-16">사물함:</span>
                            {isEditingLocker ? (
                              <span className="flex items-center gap-2 flex-1">
                                <input
                                  value={newLockerNumber}
                                  onChange={(e) => setNewLockerNumber(e.target.value)}
                                  placeholder="번호"
                                  inputMode="numeric"
                                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                                <button
                                  onClick={handleAssignLocker}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                >
                                  저장
                                </button>
                                {lockerNumber !== null && (
                                  <button
                                    onClick={handleReleaseLocker}
                                    className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                                  >
                                    비우기
                                  </button>
                                )}
                                <button
                                  onClick={() => { setIsEditingLocker(false); setNewLockerNumber(''); setLockerMsg(''); }}
                                  className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                                >
                                  취소
                                </button>
                              </span>
                            ) : (
                              <span className="flex items-center gap-2 flex-1">
                                <span className="text-gray-900">
                                  {lockerNumber !== null ? `${lockerNumber}번` : '없음'}
                                </span>
                                <button
                                  onClick={() => {
                                    setIsEditingLocker(true);
                                    setNewLockerNumber(lockerNumber !== null ? String(lockerNumber) : '');
                                    setLockerMsg('');
                                  }}
                                  className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                                >
                                  <PencilIcon className="h-3 w-3 inline" /> 변경
                                </button>
                              </span>
                            )}
                          </div>
                          {lockerMsg && (
                            <p className="text-xs text-gray-600 mb-3 ml-[4.5rem]">{lockerMsg}</p>
                          )}

                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 입실번호 관리 섹션 */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-4">입실번호 관리</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              selectedMember.checkin_code ? 'bg-green-400' : 'bg-red-400'
                            }`}
                          ></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedMember.checkin_code
                                ? `입실번호: ${selectedMember.checkin_code}`
                                : '입실번호 미배정'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {selectedMember.checkin_code
                                ? '번호입실 시 이 번호로 체크인 가능'
                                : '전화번호 중복으로 자동 배정 실패 - 직접 지정해주세요'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            maxLength={4}
                            placeholder="4자리"
                            defaultValue={selectedMember.checkin_code || ''}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-md text-center"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const value = (e.target as HTMLInputElement).value;
                                if (value.length === 4 && /^\d{4}$/.test(value)) {
                                  handleUpdateCheckinCode(selectedMember.id, value);
                                }
                              }
                            }}
                            id="checkin-code-input"
                          />
                          <button
                            onClick={() => {
                              const input = document.getElementById('checkin-code-input') as HTMLInputElement;
                              const value = input?.value || '';
                              if (value.length === 4 && /^\d{4}$/.test(value)) {
                                handleUpdateCheckinCode(selectedMember.id, value);
                              } else {
                                alert('4자리 숫자를 입력해주세요.');
                              }
                            }}
                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                          >
                            저장
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 얼굴 등록 관리 섹션 */}
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-4">얼굴 인식 관리</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              selectedMember.face_registered ? 'bg-green-400' : 'bg-gray-300'
                            }`}
                          ></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedMember.face_registered ? '얼굴 등록됨' : '얼굴 미등록'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {selectedMember.face_registered
                                ? '출입 시 얼굴 인식으로 자동 체크인 가능'
                                : '태블릿 앱에서 얼굴을 등록하세요'}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          {selectedMember.face_registered && (
                            <button
                              onClick={() => handleDeleteFaceClick(selectedMember)}
                              className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                            >
                              삭제
                            </button>
                          )}
                        </div>
                      </div>
                      {/* 앱 전용 안내 메시지 */}
                      {!selectedMember.face_registered && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                          <p className="text-xs text-blue-700">
                            <span className="font-medium">📱 얼굴 등록은 태블릿 앱에서만 가능합니다</span>
                            <br />
                            태블릿 앱에서 회원을 선택하여 얼굴을 등록해주세요.
                            <br />
                            (512차원 고정밀 얼굴 인식 사용)
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 위험 영역 - 회원 퇴원 처리 */}
                  <div className="mt-8 pt-6 border-t border-red-200">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h4 className="text-sm font-medium text-red-800 mb-2">퇴원 처리 (탈퇴)</h4>
                      <p className="text-xs text-red-600 mb-4">
                        퇴원 처리하면 회원의 시스템 접근(로그인)이 즉시 차단되고 <b>퇴원생 목록</b>으로 이동합니다. 출석 기록·이용권·결제 이력은 회계/세무 목적으로 보존됩니다. 나중에 <b>재원 복귀</b>로 되돌릴 수 있습니다.
                      </p>
                      <button
                        onClick={handleDeleteMember}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
                      >
                        <TrashIcon className="h-4 w-4" />
                        퇴원 처리
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* 기본정보 2 탭 */}
              {activeTab === 'basic2' && selectedMember && (
                <MemberBasicInfo2Tab
                  userId={String((selectedMember as any).user_id || (selectedMember as any).id || '')}
                  onGotoParentTab={() => setActiveTab('parent')}
                />
              )}

              {/* 학부모 탭 */}
              {activeTab === 'parent' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-4">학부모 1 정보</h3>
                      <div className="space-y-2">
                        {/* 학부모 1 이름 */}
                        <div className="flex items-center text-sm">
                          <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2 w-12">이름:</span>
                          {isEditingParent1Name ? (
                            <span className="flex items-center gap-1">
                              <input
                                type="text"
                                value={newParent1Name}
                                onChange={(e) => setNewParent1Name(e.target.value)}
                                placeholder="학부모 성함"
                                className="px-2 py-1 border border-gray-300 rounded text-sm w-40"
                              />
                              <button onClick={handleUpdateParent1Name} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">저장</button>
                              <button onClick={() => { setIsEditingParent1Name(false); setNewParent1Name(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300">취소</button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className="text-gray-900">{(selectedMember as any).parent1_name || <span className="text-gray-400">미등록</span>}</span>
                              <button
                                onClick={() => { setIsEditingParent1Name(true); setNewParent1Name((selectedMember as any).parent1_name || ''); }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm">
                          <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2 w-12">연락처:</span>
                          {isEditingParentPhone1 ? (
                            <span className="flex items-center gap-2">
                              <input
                                type="text"
                                value={newParentPhone1}
                                onChange={(e) => setNewParentPhone1(e.target.value)}
                                placeholder="01012345678"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={() => requestSaveParentPhone(1)}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              >
                                저장
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingParentPhone1(false);
                                  setNewParentPhone1('');
                                  setParentPhoneConfirm(null);
                                }}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                              >
                                취소
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className="text-gray-900">{selectedMember.parent_phone_1 || '미등록'}</span>
                              {(selectedMember as any).parent_phone_issue === 'invalid' && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                                  학부모 번호 확인 필요
                                </span>
                              )}
                              <button
                                onClick={() => {
                                  setIsEditingParentPhone1(true);
                                  setNewParentPhone1(selectedMember.parent_phone_1 || '');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                              <button
                                onClick={async () => {
                                  const phone = (selectedMember.parent_phone_1 || '').replace(/[-\s]/g, '');
                                  if (phone.length < 4) {
                                    alert('학부모 1 전화번호가 등록되어 있지 않습니다.');
                                    return;
                                  }
                                  if (!window.confirm(`학부모 1 계정 비밀번호를 ${phone.slice(-4)} 로 (재)설정하시겠습니까?\n• 계정이 없으면 자동 생성\n• 번호 변경/분실 시 새 번호 뒷 4자리로 재설정\n(로그인 후 바로 바꾸지 않아도 됩니다. 원하면 본인이 나중에 바꿀 수 있습니다)`)) return;
                                  try {
                                    const r = await apiClient.post(`/v1/owner/members/${selectedMember.id}/reset-parent-password?parent_slot=1`);
                                    alert(`✅ ${r.data.message}\n새 비밀번호: ${r.data.new_password}`);
                                  } catch (e: any) {
                                    alert(`비번 복구 실패: ${e.response?.data?.detail || '오류'}`);
                                  }
                                }}
                                className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs hover:bg-amber-200"
                                title="학부모1 계정 (재)설정 — 계정이 없으면 자동 생성, 있으면 비번을 전화번호 뒷 4자리로 재설정"
                              >
                                🔑 계정 생성/복구
                              </button>
                            </span>
                          )}
                        </div>
                        {renderParentPhoneConfirm(1)}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-700">알림 수신</span>
                          <button
                            onClick={handleToggleParent1Notification}
                            disabled={!selectedMember.parent_phone_1}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              !selectedMember.parent_phone_1
                                ? 'bg-gray-200 cursor-not-allowed'
                                : (selectedMember.parent1_notification_enabled ?? false)
                                  ? 'bg-blue-600'
                                  : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                selectedMember.parent_phone_1 && (selectedMember.parent1_notification_enabled ?? false)
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-4">학부모 2 정보</h3>
                      <div className="space-y-2">
                        {/* 학부모 2 이름 */}
                        <div className="flex items-center text-sm">
                          <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2 w-12">이름:</span>
                          {isEditingParent2Name ? (
                            <span className="flex items-center gap-1">
                              <input
                                type="text"
                                value={newParent2Name}
                                onChange={(e) => setNewParent2Name(e.target.value)}
                                placeholder="학부모 성함"
                                className="px-2 py-1 border border-gray-300 rounded text-sm w-40"
                              />
                              <button onClick={handleUpdateParent2Name} className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">저장</button>
                              <button onClick={() => { setIsEditingParent2Name(false); setNewParent2Name(''); }} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300">취소</button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className="text-gray-900">{(selectedMember as any).parent2_name || <span className="text-gray-400">미등록</span>}</span>
                              <button
                                onClick={() => { setIsEditingParent2Name(true); setNewParent2Name((selectedMember as any).parent2_name || ''); }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm">
                          <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2 w-12">연락처:</span>
                          {isEditingParentPhone2 ? (
                            <span className="flex items-center gap-2">
                              <input
                                type="text"
                                value={newParentPhone2}
                                onChange={(e) => setNewParentPhone2(e.target.value)}
                                placeholder="01012345678"
                                className="px-2 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={() => requestSaveParentPhone(2)}
                                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                              >
                                저장
                              </button>
                              <button
                                onClick={() => {
                                  setIsEditingParentPhone2(false);
                                  setNewParentPhone2('');
                                  setParentPhoneConfirm(null);
                                }}
                                className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                              >
                                취소
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className="text-gray-900">{selectedMember.parent_phone_2 || '미등록'}</span>
                              <button
                                onClick={() => {
                                  setIsEditingParentPhone2(true);
                                  setNewParentPhone2(selectedMember.parent_phone_2 || '');
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200"
                              >
                                <PencilIcon className="h-3 w-3 inline" /> 변경
                              </button>
                            </span>
                          )}
                        </div>
                        {renderParentPhoneConfirm(2)}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-700">알림 수신</span>
                          <button
                            onClick={handleToggleParent2Notification}
                            disabled={!selectedMember.parent_phone_2}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              !selectedMember.parent_phone_2
                                ? 'bg-gray-200 cursor-not-allowed'
                                : (selectedMember.parent2_notification_enabled ?? false)
                                  ? 'bg-blue-600'
                                  : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                selectedMember.parent_phone_2 && (selectedMember.parent2_notification_enabled ?? false)
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 대표 학부모 선택 */}
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-4">대표 학부모 설정</h3>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        알림 우선 수신 학부모
                      </label>
                      <div className="flex gap-6">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="primary_parent"
                            value="1"
                            checked={(selectedMember.primary_parent || 1) === 1}
                            onChange={() => handleUpdatePrimaryParent(1)}
                            className="w-4 h-4 mr-2"
                          />
                          <span className="text-sm text-gray-700">학부모 1</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="primary_parent"
                            value="2"
                            checked={selectedMember.primary_parent === 2}
                            onChange={() => handleUpdatePrimaryParent(2)}
                            className="w-4 h-4 mr-2"
                          />
                          <span className="text-sm text-gray-700">학부모 2</span>
                        </label>
                      </div>
                      <p className="mt-3 text-xs text-gray-600">
                        💡 알림 수신이 활성화된 학부모에게만 출입 알림톡이 전송됩니다.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'attendance' && (
                <div>
                  {/* ⭐ 2026-06-14 지각 이력 (출결 영역 — 출석/미출석과 분리, 도착시각 기준) */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-medium text-gray-900">🕐 지각 이력 (최근 {attLateDays}일)</h3>
                      {attLateLoading && <span className="text-sm text-gray-500">불러오는 중...</span>}
                    </div>
                    {attLateRecords.length > 0 ? (
                      <>
                        <div className="space-y-2">
                          {attLateRecords.map((day) => (
                            <div key={day.date} className="p-3 bg-white border rounded-lg" style={{ borderLeft: '4px solid #f59e0b' }}>
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-medium text-gray-900">{new Date(day.date).toLocaleDateString('ko-KR')}</span>
                                <span className="text-red-600 font-bold">총 {day.total_minutes}분 지각 ({day.periods.length}교시)</span>
                              </div>
                              <div className="flex flex-col gap-0.5 pl-1">
                                {day.periods.map((p) => (
                                  <span key={p.period_number} className="text-sm text-gray-600">
                                    {p.period_label}: {p.expected} 예정 → {p.actual} 입실 · <span className="text-orange-600 font-medium">{p.minutes_late}분</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="text-center mt-3">
                          <button
                            onClick={() => setAttLateDays((d) => d + 30)}
                            disabled={attLateLoading}
                            className="px-4 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                          >
                            더보기 (+30일)
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-5 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">최근 {attLateDays}일간 지각 기록이 없습니다</p>
                        <button
                          onClick={() => setAttLateDays((d) => d + 30)}
                          disabled={attLateLoading}
                          className="mt-2 px-4 py-1.5 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                        >
                          더 이전 기록 보기 (+30일)
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">최근 출입 기록</h3>
                    {loadingDetail && (
                      <div className="flex items-center text-sm text-gray-500">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        로딩 중...
                      </div>
                    )}
                  </div>

                  {selectedMember.attendanceHistory && selectedMember.attendanceHistory.length > 0 ? (
                    <div className="space-y-3">
                      {selectedMember.attendanceHistory.map((record, index) => (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              {/* 입장/퇴장/외출 아이콘 */}
                              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                record.action === 'entry'
                                  ? 'bg-green-100 text-green-600'
                                  : record.action === 'outing'
                                  ? 'bg-yellow-100 text-yellow-600'
                                  : 'bg-blue-100 text-blue-600'
                              }`}>
                                {record.action === 'entry' ? (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                  </svg>
                                )}
                              </div>

                              {/* 날짜 및 시간 */}
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className={`font-semibold text-sm ${
                                    record.action === 'entry'
                                      ? 'text-green-700'
                                      : record.action === 'outing'
                                      ? 'text-yellow-700'
                                      : 'text-blue-700'
                                  }`}>
                                    {record.action === 'entry'
                                      ? '입장'
                                      : record.action === 'outing'
                                      ? `외출${record.outing_type ? ` (${getOutingTypeLabel(record.outing_type)})` : ''}`
                                      : '퇴장'}
                                  </span>
                                  <span className="text-sm text-gray-500">•</span>
                                  <span className="text-sm text-gray-600">{formatMethod(record.method)}</span>
                                </div>
                                <div className="text-sm text-gray-900 mt-1">
                                  {formatDateTime(record.date)}
                                </div>
                                {record.gate_name && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {record.gate_name}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">출입 기록이 없습니다</p>
                    </div>
                  )}
                </div>
              )}

              {/* 이용권 탭 */}
              {activeTab === 'ticket' && (
                <div className="space-y-6">
                  {/* 이용 정보 요약 */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-4">이용 정보</h3>
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">가입일:</span>
                          <span className="text-gray-900">{formatDateTime(selectedMember.createdAt)}</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">만료일:</span>
                          <span className={`font-medium ${
                            !selectedMember.expiryDate
                              ? 'text-gray-400'
                              : new Date(selectedMember.expiryDate) < new Date()
                              ? 'text-red-600'
                              : new Date(selectedMember.expiryDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}>
                            {formatExpiryDate(selectedMember.expiryDate)}
                          </span>
                        </div>
                        <div className="flex items-center text-sm">
                          <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">총 이용시간:</span>
                          <span className="text-gray-900">{selectedMember.totalUsageHours}시간</span>
                        </div>
                        <div className="flex items-center text-sm">
                          <CreditCardIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-gray-500 mr-2">이용권:</span>
                          <span>
                            {getMembershipBadge(
                              selectedMember.membershipType,
                              selectedMember.remainingTime,
                              selectedMember.expiryDate,
                              selectedMember.voucherName
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 결제 내역 섹션 */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">결제 내역</h3>
                      {isLoadingPayments && (
                        <div className="flex items-center text-sm text-gray-500">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          로딩 중...
                        </div>
                      )}
                    </div>

                  {isLoadingPayments ? (
                    <div className="text-center py-16">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-lg font-medium text-gray-700">결제 내역을 불러오는 중...</p>
                    </div>
                  ) : paymentHistory.length > 0 ? (
                    <div className="space-y-4">
                      {/* 활성 구독 정보 */}
                      {paymentHistory.some((p) => p.subscription_status === 'active') && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center">
                            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                            <span className="text-sm font-medium text-green-900">활성 이용권</span>
                          </div>
                          {paymentHistory
                            .filter((p) => p.subscription_status === 'active')
                            .map((payment) => (
                              <div key={payment.payment_id} className="mt-2 ml-7">
                                <p className="text-sm text-green-800">
                                  {payment.plan_name} • {payment.subscription_code}
                                </p>
                                <p className="text-xs text-green-600">
                                  {formatDateTime(payment.start_date)} ~ {formatDateTime(payment.end_date)}
                                </p>
                              </div>
                            ))}
                        </div>
                      )}

                      {/* 결제 내역 목록 */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                결제일
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                이용권
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                금액
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                결제 방법
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                이용권 코드
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                이용 기간
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                상태
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                메모
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {paymentHistory.map((payment) => (
                              <tr key={payment.payment_id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDateTime(payment.payment_date)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {payment.plan_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {(payment.discount_amount ?? 0) > 0 ? (
                                    <div>
                                      <div className="font-semibold text-blue-700">{payment.amount.toLocaleString()}원</div>
                                      <div className="text-xs text-gray-400 line-through">
                                        {(payment.original_price ?? (payment.amount + (payment.discount_amount ?? 0))).toLocaleString()}원
                                      </div>
                                      <div className="text-xs text-red-600">
                                        💸 -{(payment.discount_amount ?? 0).toLocaleString()}원
                                        {payment.discount_reason_name && (
                                          <span className="ml-1 text-gray-500">({payment.discount_reason_name}{payment.discount_reason_text ? `: ${payment.discount_reason_text}` : ''})</span>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <>{payment.amount.toLocaleString()}원</>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      payment.payment_method === 'cash'
                                        ? 'bg-green-100 text-green-800'
                                        : payment.payment_method === 'transfer'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {payment.payment_method === 'cash'
                                      ? '현금'
                                      : payment.payment_method === 'transfer'
                                      ? '계좌이체'
                                      : payment.payment_method}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                  {payment.subscription_code}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <div>
                                    <div>{formatDateTime(payment.start_date)}</div>
                                    <div className="text-xs text-gray-400">
                                      ~ {formatDateTime(payment.end_date)}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      payment.subscription_status === 'active'
                                        ? 'bg-green-100 text-green-800'
                                        : payment.subscription_status === 'expired'
                                        ? 'bg-gray-100 text-gray-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {payment.subscription_status === 'active'
                                      ? '사용중'
                                      : payment.subscription_status === 'expired'
                                      ? '만료'
                                      : payment.subscription_status === 'cancelled'
                                      ? '취소됨'
                                      : payment.subscription_status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700 max-w-xs">
                                  {payment.description ? (
                                    <span className="whitespace-pre-wrap break-all" title={payment.description}>
                                      {payment.description}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* 연기 이력 */}
                      {extensionHistory.length > 0 && (
                        <div className="mt-6 border-t pt-6">
                          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                            <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                            연기 이력
                            <span className="ml-2 text-sm font-normal text-gray-500">총 {extensionHistory.length}회</span>
                          </h3>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-amber-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">이용권</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">연기일수</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">변경 전</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">변경 후</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">사유</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">처리일</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {extensionHistory.map((ext, idx) => (
                                  <tr key={idx} className="hover:bg-amber-50">
                                    <td className="px-4 py-2 text-sm text-gray-900">{ext.voucher_name || '-'}</td>
                                    <td className="px-4 py-2 text-sm font-medium text-amber-600">+{ext.extended_days}일</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{ext.old_end_date ? new Date(ext.old_end_date).toLocaleDateString('ko-KR') : '-'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-900 font-medium">{ext.new_end_date ? new Date(ext.new_end_date).toLocaleDateString('ko-KR') : '-'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{ext.reason || '-'}</td>
                                    <td className="px-4 py-2 text-sm text-gray-500">{ext.created_at ? new Date(ext.created_at).toLocaleDateString('ko-KR') : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* 결제 통계 요약 */}
                      <div className="mt-6 grid grid-cols-3 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600">총 결제 횟수</p>
                          <p className="text-2xl font-bold text-blue-600">{paymentHistory.length}회</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600">총 결제 금액</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {paymentHistory.reduce((sum, p) => sum + p.amount, 0).toLocaleString()}원
                          </p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600">활성 이용권</p>
                          <p className="text-2xl font-bold text-green-600">
                            {paymentHistory.filter((p) => p.subscription_status === 'active').length}개
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCardIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">결제 내역이 없습니다</p>
                    </div>
                  )}
                  </div>
                </div>
              )}

              {/* 스케줄 탭 — 위: 주간 패턴 / 아래: 월간 캘린더 */}
              {activeTab === 'schedule' && (
                <div className="space-y-6">
                  {isLoadingSchedule ? (
                    <div className="text-center py-16">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-lg font-medium text-gray-700">스케줄을 불러오는 중...</p>
                    </div>
                  ) : (
                    <>
                      {/* 스케줄 유형 표시 */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          scheduleData.schedule_type === 'period'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {scheduleData.schedule_type === 'period' ? '교시 스케줄' : '주간 스케줄'}
                        </span>
                      </div>

                      {/* ⭐ 2026-06-14 학생이 시간으로 등록했으면 그 시간 표시 (교시는 자동 환산) */}
                      {Object.values(scheduleRanges).some((v) => (v || []).length > 0) && (
                        <div className="mb-3 p-3 rounded-lg" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                          <div className="text-sm font-bold mb-1" style={{ color: '#166534' }}>🕐 학생이 입력한 시간</div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {['0', '1', '2', '3', '4', '5', '6']
                              .filter((d) => (scheduleRanges[d] || []).length > 0)
                              .map((d) => (
                                <span key={d} className="text-xs" style={{ color: '#15803d' }}>
                                  <strong>{['월', '화', '수', '목', '금', '토', '일'][Number(d)]}</strong>{' '}
                                  {(scheduleRanges[d] || []).map((r) => `${r.start}~${r.end}`).join(', ')}
                                </span>
                              ))}
                          </div>
                          <div className="text-xs mt-1" style={{ color: '#64748b' }}>아래 교시표는 이 시간에서 자동 환산된 것입니다.</div>
                        </div>
                      )}

                      {/* 주간 스케줄 (weekly 모드) 또는 교시 스케줄 (period 모드) */}
                      <div>
                        {scheduleData.schedule_type === 'period' ? (
                          (() => {
                            const PERIOD_DAYS = ['월', '화', '수', '목', '금', '토', '일'];
                            const blocks = scheduleData.period_blocks.filter(b => b.student_id === selectedMember?.id);

                            // period_blocks → { dayIndex: [period_numbers] } 맵 변환
                            const checkedMap: Record<string, number[]> = {};
                            blocks.forEach(b => {
                              const key = String(b.day_of_week);
                              if (!checkedMap[key]) checkedMap[key] = [];
                              checkedMap[key].push(...b.periods);
                            });

                            const templates = periodTemplates.filter(t => t.period_type !== 'break' && t.is_active !== false);

                            if (templates.length === 0 && blocks.length === 0) {
                              return (
                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                  <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                  <p className="text-gray-500">등록된 교시 스케줄이 없습니다</p>
                                  <p className="text-sm text-gray-400 mt-1">교시 설정 페이지에서 스케줄을 등록할 수 있습니다</p>
                                </div>
                              );
                            }

                            return (
                              <div style={{ overflowX: 'auto' }}>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: `minmax(90px, 130px) repeat(7, 1fr)`,
                                  minWidth: '480px',
                                }}>
                                  {/* 헤더 */}
                                  <div style={{ padding: '8px', fontWeight: 700, backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd', fontSize: '13px' }}>
                                    교시
                                  </div>
                                  {PERIOD_DAYS.map((day, idx) => (
                                    <div key={idx} style={{ padding: '8px', fontWeight: 700, backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd', textAlign: 'center', fontSize: '13px' }}>
                                      {day}
                                    </div>
                                  ))}

                                  {/* 교시 행 */}
                                  {templates.map((template) => (
                                    <React.Fragment key={template.period_number}>
                                      <div style={{
                                        padding: '6px 8px',
                                        borderBottom: '1px solid #eee',
                                        backgroundColor: '#fff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        minHeight: '44px',
                                      }}>
                                        <span style={{ fontWeight: 600, fontSize: '13px' }}>{template.period_name}</span>
                                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>{template.start_time}~{template.end_time}</span>
                                      </div>
                                      {PERIOD_DAYS.map((_, dayIdx) => {
                                        const isChecked = (checkedMap[String(dayIdx)] || []).includes(template.period_number);
                                        return (
                                          <div key={dayIdx} style={{
                                            borderBottom: '1px solid #eee',
                                            borderRight: '1px solid #f0f0f0',
                                            backgroundColor: isChecked ? '#2196f3' : '#fafafa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: '44px',
                                          }}>
                                            {isChecked && (
                                              <CheckIcon style={{ color: 'white', width: '20px', height: '20px' }} />
                                            )}
                                          </div>
                                        );
                                      })}
                                    </React.Fragment>
                                  ))}
                                </div>

                                {/* 범례 */}
                                <div style={{ display: 'flex', gap: '16px', marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ display: 'inline-block', width: '14px', height: '14px', backgroundColor: '#2196f3', borderRadius: '2px' }}></span>
                                    수강
                                  </span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ display: 'inline-block', width: '14px', height: '14px', backgroundColor: '#fafafa', border: '1px solid #ddd', borderRadius: '2px' }}></span>
                                    미수강
                                  </span>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <>
                            <h3 className="text-lg font-medium text-gray-900 mb-4">주간 스케줄</h3>
                            {scheduleData.study_schedules.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">요일</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">입실 예정</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">퇴실 예정</th>
                                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {scheduleData.study_schedules.map((schedule) => (
                                      <tr key={schedule.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                          {schedule.day_name}요일
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                          {schedule.expected_check_in || '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                          {schedule.expected_check_out || '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            schedule.is_active
                                              ? 'bg-green-100 text-green-800'
                                              : 'bg-gray-100 text-gray-800'
                                          }`}>
                                            {schedule.is_active ? '활성' : '비활성'}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-center py-8 bg-gray-50 rounded-lg">
                                <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500">등록된 주간 스케줄이 없습니다</p>
                                <p className="text-sm text-gray-400 mt-1">학부모 앱에서 스케줄을 등록할 수 있습니다</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* 학원 스케줄 */}
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">학원 스케줄 (외출)</h3>
                        {scheduleData.academy_schedules.length > 0 ? (
                          <div className="space-y-3">
                            {scheduleData.academy_schedules.map((schedule) => (
                              <div
                                key={schedule.id}
                                className={`p-4 rounded-lg border ${
                                  schedule.is_today
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-white border-gray-200'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <h4 className="font-medium text-gray-900">{schedule.academy_name}</h4>
                                    <p className="text-sm text-gray-600 mt-1">
                                      {schedule.start_time} ~ {schedule.end_time}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {schedule.days_of_week.map((d: number) => ['', '월', '화', '수', '목', '금', '토', '일'][d] || d).join(', ')}요일
                                    </p>
                                  </div>
                                  {schedule.is_today && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      오늘
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500">등록된 학원 스케줄이 없습니다</p>
                            <p className="text-sm text-gray-400 mt-1">학부모 앱에서 학원 외출 스케줄을 등록할 수 있습니다</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* 순찰기록 탭 */}
              {activeTab === 'patrol' && (
                <div className="space-y-6">
                  {isLoadingPatrol ? (
                    <div className="text-center py-16">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-lg font-medium text-gray-700">순찰 기록을 불러오는 중...</p>
                    </div>
                  ) : (
                    <>
                      {/* 통계 요약 */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-gray-900">{patrolStats.total_count}</p>
                          <p className="text-sm text-gray-500">총 기록</p>
                        </div>
                        <div className="bg-yellow-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-yellow-600">{patrolStats.type_counts['sleep'] || 0}</p>
                          <p className="text-sm text-gray-500">수면</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-red-600">{patrolStats.type_counts['off_task'] || 0}</p>
                          <p className="text-sm text-gray-500">딴짓</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-blue-600">{patrolStats.type_counts['leave'] || 0}</p>
                          <p className="text-sm text-gray-500">자리비움</p>
                        </div>
                      </div>

                      {/* 순찰 기록 목록 */}
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">최근 30일 기록</h3>
                        {patrolNotes.length > 0 ? (
                          <div className="space-y-3">
                            {patrolNotes.map((note) => (
                              <div key={note.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900">{note.note_type_label}</span>
                                      <span className="text-sm text-gray-500">({note.seat_code})</span>
                                    </div>
                                    {note.note_text && (
                                      <p className="text-sm text-gray-600 mt-1">{note.note_text}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-2">
                                      {note.staff_name} · {new Date(note.created_at).toLocaleString('ko-KR')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <ClipboardDocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500">순찰 기록이 없습니다</p>
                            <p className="text-sm text-gray-400 mt-1">출석부에서 순찰 노트를 작성하면 여기에 표시됩니다</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* 하단: 월간 계획 캘린더 (학생이 작성한 일별 출결 패턴 + 사유서) */}
                  {selectedMember && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        📅 월간 계획 (학생 작성)
                      </h3>
                      <MemberMonthlyScheduleView studentId={selectedMember.id} />
                    </div>
                  )}
                </div>
              )}

              {/* 벌점 탭 */}
              {activeTab === 'penalty' && (
                <div className="space-y-6">
                  {isLoadingPenalty ? (
                    <div className="text-center py-16">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
                      <p className="text-lg font-medium text-gray-700">벌점 정보를 불러오는 중...</p>
                    </div>
                  ) : (
                    <>
                      {/* 벌점 현황 요약 */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-red-600">{penaltySummary?.current_points || 0}점</p>
                          <p className="text-sm text-gray-500">현재 벌점</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-gray-600">{penaltySummary?.total_points_ever || 0}점</p>
                          <p className="text-sm text-gray-500">누적 벌점</p>
                        </div>
                      </div>

                      {/* 벌점 기록 목록 */}
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">벌점 부과 내역</h3>
                        {penaltyRecords.length > 0 ? (
                          <div className="space-y-3">
                            {penaltyRecords.map((record) => (
                              <div
                                key={record.id}
                                onClick={() => setPenaltyDetailId(record.id)}
                                title="클릭하면 상세(예정 시각·실제 도착 시각·지각 시간)를 볼 수 있습니다"
                                className={`p-4 bg-white border rounded-lg hover:bg-blue-50 cursor-pointer ${
                                  record.is_cancelled ? 'opacity-50' : ''
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      {/* absence/late_arrival 같은 코드가 그대로 보이던 문제 — 한글 라벨로 */}
                                      <span className="font-medium text-gray-900">
                                        {({
                                          absence: '결석',
                                          late_arrival: '지각',
                                          early_departure: '조퇴',
                                        } as Record<string, string>)[record.penalty_type] || record.penalty_type}
                                      </span>
                                      <span className="text-red-600 font-bold">-{record.points}점</span>
                                      {(() => {
                                        const b = penaltySourceBadge((record as any).reference_type, record.is_auto);
                                        return <span className={`px-2 py-0.5 text-xs rounded ${b.className}`}>{b.label}</span>;
                                      })()}
                                      {record.reason?.startsWith('[자율학습]') && (
                                        <span className="px-2 py-0.5 text-xs bg-rose-100 text-rose-700 rounded">자율학습</span>
                                      )}
                                      {record.is_cancelled && (
                                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">취소됨</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">
                                      {record.reason?.replace('[자율학습]', '').trim()}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-2">
                                      {new Date(record.occurred_date).toLocaleDateString('ko-KR')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-500">벌점 기록이 없습니다</p>
                            <p className="text-sm text-gray-400 mt-1">벌점 관리 메뉴에서 벌점을 부과할 수 있습니다</p>
                          </div>
                        )}
                      </div>

                      {/* 지각 기록 */}
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">📋 지각 기록 (최근 30일)</h3>
                        {lateRecords.length > 0 ? (
                          <div className="space-y-3">
                            {lateRecords.map((record) => (
                              <div
                                key={record.id}
                                className="p-4 bg-white border rounded-lg hover:bg-gray-50"
                                style={{ borderLeft: '4px solid #f59e0b' }}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900">
                                        {new Date(record.record_date).toLocaleDateString('ko-KR')}
                                      </span>
                                      <span className="text-red-600 font-bold">{record.minutes_late}분 지각</span>
                                      {record.penalty_issued && (
                                        <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">벌점 부과됨</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">
                                      {record.expected_time?.substring(0, 5)} 예정 → {record.actual_time?.substring(0, 5)} 입실
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-6 bg-gray-50 rounded-lg">
                            <p className="text-gray-500">최근 30일간 지각 기록이 없습니다</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* 사유서 탭 — 해당 학생의 사유서 이력 (읽기전용) */}
              {activeTab === 'exception' && (
                <div className="space-y-4">
                  {isLoadingException ? (
                    <div className="text-center py-16">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-lg font-medium text-gray-700">사유서 이력을 불러오는 중...</p>
                    </div>
                  ) : exceptionRecords.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">제출된 사유서가 없습니다</p>
                      <p className="text-sm text-gray-400 mt-1">학생·학부모가 제출한 사유서(결석/지각/조퇴/외출)가 여기에 표시됩니다</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">사유서 이력</h3>
                        <span className="text-sm text-gray-500">총 {exceptionRecords.length}건</span>
                      </div>
                      <div className="space-y-3">
                        {exceptionRecords.map((exc) => {
                          const statusLabel: Record<string, string> = {
                            pending: '부모승인 대기',
                            parent_approved: '독서실 승인 대기',
                            approved: '승인',
                            rejected: '거절',
                          };
                          const statusColor: Record<string, string> = {
                            pending: 'bg-yellow-100 text-yellow-800',
                            parent_approved: 'bg-blue-100 text-blue-800',
                            approved: 'bg-green-100 text-green-800',
                            rejected: 'bg-red-100 text-red-800',
                          };
                          const fmtDate = (s?: string | null) => {
                            if (!s) return '-';
                            try { return new Date(s).toLocaleDateString('ko-KR'); } catch { return s; }
                          };
                          return (
                            <div key={exc.id} className="p-4 bg-white border rounded-lg">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-gray-900">{fmtDate(exc.exception_date)}</span>
                                  {exc.exception_type && (
                                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{exc.exception_type}</span>
                                  )}
                                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColor[exc.status] || 'bg-gray-100 text-gray-700'}`}>
                                    {statusLabel[exc.status] || exc.status}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                  {exc.submitter_role === 'student' ? '학생 제출' : exc.submitter_role === 'parent' ? '학부모 제출' : ''}
                                </span>
                              </div>
                              {formatExceptionTime(exc.exception_type, exc.expected_arrival_time, exc.expected_departure_time) && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatExceptionTime(exc.exception_type, exc.expected_arrival_time, exc.expected_departure_time)}
                                </p>
                              )}
                              {exc.reason && (
                                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{exc.reason}</p>
                              )}
                              {exc.review_note && (
                                <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded px-2 py-1">
                                  <span className="text-gray-400">운영자 메모:</span> {exc.review_note}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-2">
                                제출 {fmtDate(exc.created_at)}
                                {exc.reviewed_at && <> · 처리 {fmtDate(exc.reviewed_at)}</>}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-400 text-center pt-2">
                        승인·거절 처리는 <b>사유서 관리</b> 메뉴에서 할 수 있습니다.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* 학습관리 탭 */}
              {activeTab === 'study' && (
                <div className="space-y-6">
                  {/* 기간 선택 — 점주 요청(2026-07-27): 학생별로 기간 골라 집계 */}
                  <div className="flex flex-wrap items-center gap-2">
                    {([
                      { key: '7d', label: '최근 7일' },
                      { key: '30d', label: '최근 30일' },
                      { key: 'month', label: '이번 달' },
                      { key: 'custom', label: '직접 선택' },
                    ] as const).map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setStudyRange(r.key)}
                        className={`text-xs px-3 py-1.5 rounded-full border ${
                          studyRange === r.key
                            ? 'bg-blue-50 border-blue-400 text-blue-700 font-semibold'
                            : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                    {studyRange === 'custom' && (
                      <div className="flex items-center gap-1 ml-1">
                        <input
                          type="date"
                          value={studyStart}
                          onChange={(e) => setStudyStart(e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1 text-xs"
                        />
                        <span className="text-gray-400 text-xs">~</span>
                        <input
                          type="date"
                          value={studyEnd}
                          onChange={(e) => setStudyEnd(e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1 text-xs"
                        />
                      </div>
                    )}
                  </div>

                  {isLoadingStudyData ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">학습 데이터 로딩 중...</span>
                    </div>
                  ) : studyError ? (
                    /* 실패는 '데이터 없음'과 반드시 구분 — 점주가 데이터가 없는 걸로 오해했던 사례 */
                    <div className="text-center py-8 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-700 font-medium">{studyError}</p>
                      <button
                        type="button"
                        onClick={() => setStudyRange((r) => r)}
                        className="mt-3 px-4 py-2 text-sm rounded bg-red-600 text-white font-semibold"
                      >
                        다시 시도
                      </button>
                    </div>
                  ) : studySummary ? (
                    <>
                      {/* 기간 요약 */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">📚 순공부시간</h3>
                        <p className="text-xs text-gray-500 mb-4">
                          {studySummary.start_date} ~ {studySummary.end_date}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                            <p className="text-xs text-gray-500 mb-1">기간 합계</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {Math.floor(studySummary.total_minutes / 60)}시간 {studySummary.total_minutes % 60}분
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                            <p className="text-xs text-gray-500 mb-1">오늘</p>
                            <p className="text-2xl font-bold text-indigo-600">
                              {Math.floor(studySummary.today_minutes / 60)}시간 {studySummary.today_minutes % 60}분
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                            <p className="text-xs text-gray-500 mb-1">나온 날</p>
                            <p className="text-2xl font-bold text-gray-800">
                              {studySummary.study_days}<span className="text-sm text-gray-400"> / {studySummary.total_days}일</span>
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                            <p className="text-xs text-gray-500 mb-1">나온 날 평균</p>
                            <p className="text-2xl font-bold text-gray-800">
                              {Math.floor(studySummary.avg_minutes_on_study_days / 60)}시간 {studySummary.avg_minutes_on_study_days % 60}분
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 오늘 세션 목록 */}
                      {studyTimeData?.study_sessions && studyTimeData.study_sessions.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">🕐 오늘 학습 세션</h3>
                          <div className="space-y-3">
                            {studyTimeData.study_sessions.map((session: any, index: number) => (
                              <div key={session.session_id || index} className="bg-white border rounded-lg p-4 flex justify-between items-center">
                                <div className="flex items-center space-x-4">
                                  <div className={`w-3 h-3 rounded-full ${session.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {session.check_in} ~ {session.check_out}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {session.status === 'active' ? '공부 중' : '완료'}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-blue-600">
                                    {Math.floor((session.pure_study_minutes || 0) / 60)}시간 {(session.pure_study_minutes || 0) % 60}분
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 날짜별 기록 (최신순) */}
                      {studyTimeHistory.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 날짜별 기록</h3>
                          <div className="bg-white border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">순공부시간</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {studyTimeHistory.map((item, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{item.date}</td>
                                    <td className={`px-4 py-3 text-sm text-right font-medium ${
                                      item.total_minutes > 0 ? 'text-blue-600' : 'text-gray-300'
                                    }`}>
                                      {item.total_minutes > 0
                                        ? `${Math.floor(item.total_minutes / 60)}시간 ${item.total_minutes % 60}분`
                                        : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {studySummary.total_minutes === 0 && (
                        <div className="text-center py-6 bg-gray-50 rounded-lg">
                          <ClockIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                          <p className="text-gray-500">이 기간에는 학습 기록이 없습니다</p>
                          <p className="text-sm text-gray-400 mt-1">출입 기록이 있으면 순공부시간이 자동으로 계산됩니다</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">학습 데이터가 없습니다</p>
                      <p className="text-sm text-gray-400 mt-1">출입 기록이 있으면 순공부시간이 자동으로 계산됩니다</p>
                    </div>
                  )}
                </div>
              )}
              {/* 학부모소통 탭 */}
              {activeTab === 'communication' && (
                <div className="space-y-6">
                  {isLoadingComm ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">소통 내역 로딩 중...</span>
                    </div>
                  ) : (
                    <>
                      {/* 글 작성 영역 */}
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h3 className="text-sm font-semibold text-blue-900 mb-3">새 글 작성</h3>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <select
                              value={commNewType}
                              onChange={(e) => setCommNewType(e.target.value as 'individual' | 'announcement')}
                              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                            >
                              <option value="individual">{selectedMember?.name} 학부모에게</option>
                              <option value="announcement">전체 공지</option>
                            </select>
                          </div>
                          <input
                            type="text"
                            value={commNewTitle}
                            onChange={(e) => setCommNewTitle(e.target.value)}
                            placeholder="제목을 입력하세요"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          />
                          <textarea
                            value={commNewContent}
                            onChange={(e) => setCommNewContent(e.target.value)}
                            placeholder="내용을 입력하세요"
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                          />
                          <div className="flex justify-end">
                            <button
                              onClick={handleCommSubmitPost}
                              disabled={isSubmittingComm || !commNewTitle.trim() || !commNewContent.trim()}
                              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSubmittingComm ? '작성 중...' : '글 작성'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* 글 상세 보기 */}
                      {commSelectedPost && (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <span className={`inline-block px-2 py-0.5 text-xs rounded mr-2 ${
                                commSelectedPost.post_type === 'announcement'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {commSelectedPost.post_type === 'announcement' ? '전체공지' : '개별'}
                              </span>
                              <span className="font-semibold text-gray-900">{commSelectedPost.title}</span>
                            </div>
                            <button
                              onClick={() => { setCommSelectedPost(null); setCommComments([]); }}
                              className="text-gray-400 hover:text-gray-600 text-sm"
                            >
                              ✕ 닫기
                            </button>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap mb-4">{commSelectedPost.content}</p>
                          <p className="text-xs text-gray-400 mb-4">
                            {commSelectedPost.author_name} · {new Date(commSelectedPost.created_at).toLocaleString('ko-KR')}
                            {commSelectedPost.read_count > 0 && ` · 읽음 ${commSelectedPost.read_count}`}
                          </p>

                          {/* 댓글 목록 */}
                          <div className="border-t pt-3">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">댓글 ({commComments.length})</h4>
                            {commComments.length > 0 ? (
                              <div className="space-y-2 mb-3">
                                {commComments.map((comment) => (
                                  <div key={comment.id} className={`p-3 rounded-lg text-sm ${
                                    comment.author_type === 'owner' ? 'bg-blue-50' : 'bg-gray-50'
                                  }`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-gray-900">{comment.author_name}</span>
                                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                                        comment.author_type === 'owner'
                                          ? 'bg-blue-200 text-blue-800'
                                          : 'bg-gray-200 text-gray-600'
                                      }`}>
                                        {comment.author_type === 'owner' ? '관리자' : '학부모'}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {new Date(comment.created_at).toLocaleString('ko-KR')}
                                      </span>
                                    </div>
                                    <p className="text-gray-700">{comment.content}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 mb-3">아직 댓글이 없습니다</p>
                            )}

                            {/* 댓글 작성 */}
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={commNewComment}
                                onChange={(e) => setCommNewComment(e.target.value)}
                                placeholder="댓글을 입력하세요"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                onKeyDown={(e) => { if (e.key === 'Enter') handleCommSubmitComment(); }}
                              />
                              <button
                                onClick={handleCommSubmitComment}
                                disabled={!commNewComment.trim()}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                              >
                                전송
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 글 목록 */}
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">
                          소통 내역 ({commPosts.length}건)
                        </h3>
                        {commPosts.length > 0 ? (
                          <div className="space-y-2">
                            {commPosts.map((post) => (
                              <div
                                key={post.id}
                                onClick={() => loadCommPostDetail(post.id)}
                                className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                                  commSelectedPost?.id === post.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      {post.is_pinned && <span className="text-yellow-500 text-xs">📌</span>}
                                      <span className={`px-2 py-0.5 text-xs rounded ${
                                        post.post_type === 'announcement'
                                          ? 'bg-green-100 text-green-700'
                                          : 'bg-purple-100 text-purple-700'
                                      }`}>
                                        {post.post_type === 'announcement' ? '전체공지' : '개별'}
                                      </span>
                                      <span className="font-medium text-gray-900 text-sm truncate">{post.title}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 truncate">{post.content}</p>
                                  </div>
                                  <div className="text-right ml-4 flex-shrink-0">
                                    <p className="text-xs text-gray-400">
                                      {new Date(post.created_at).toLocaleDateString('ko-KR')}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                                      {post.read_count > 0 && <span>읽음 {post.read_count}</span>}
                                      {post.comment_count > 0 && <span>댓글 {post.comment_count}</span>}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <p className="text-gray-500">소통 내역이 없습니다</p>
                            <p className="text-sm text-gray-400 mt-1">위에서 새 글을 작성해보세요</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

              {/* 메모 탭 */}
              {activeTab === 'memo' && (
                <div className="space-y-4 p-6">
                  {/* 메모 작성 */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center gap-3 mb-3">
                      <select
                        value={newMemoCategory}
                        onChange={(e) => setNewMemoCategory(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      >
                        {Object.entries(MEMO_CATEGORIES).map(([key, label]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                      <span className="text-sm text-gray-500">분류를 선택하세요</span>
                    </div>
                    <textarea
                      value={newMemoContent}
                      onChange={(e) => setNewMemoContent(e.target.value)}
                      placeholder="메모를 입력하세요..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={handleMemoSubmit}
                        disabled={!newMemoContent.trim()}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        메모 저장
                      </button>
                    </div>
                  </div>

                  {/* 메모 목록 */}
                  {isLoadingMemos ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">메모 로딩 중...</span>
                    </div>
                  ) : memos.length > 0 ? (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-gray-700">
                        메모 기록 ({memosTotal}건)
                      </h3>
                      {memos.map((memo) => (
                        <div
                          key={memo.id}
                          className={`p-4 border rounded-lg ${memo.is_pinned ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-white'}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2 mb-2">
                              {memo.is_pinned && <span className="text-yellow-500 text-xs">📌</span>}
                              <span className={`px-2 py-0.5 text-xs rounded ${MEMO_CATEGORY_COLORS[memo.category] || 'bg-gray-100 text-gray-700'}`}>
                                {MEMO_CATEGORIES[memo.category] || memo.category}
                              </span>
                              <span className="text-xs text-gray-400">
                                {memo.author_name} · {new Date(memo.created_at).toLocaleDateString('ko-KR')} {new Date(memo.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleMemoPinToggle(memo.id, memo.is_pinned)}
                                className={`p-1 rounded text-xs ${memo.is_pinned ? 'text-yellow-600 hover:bg-yellow-100' : 'text-gray-400 hover:bg-gray-100'}`}
                                title={memo.is_pinned ? '고정 해제' : '상단 고정'}
                              >
                                📌
                              </button>
                              <button
                                onClick={() => { setEditingMemoId(memo.id); setEditMemoContent(memo.content); }}
                                className="p-1 rounded text-xs text-gray-400 hover:bg-gray-100"
                                title="수정"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => handleMemoDelete(memo.id)}
                                className="p-1 rounded text-xs text-gray-400 hover:bg-red-100"
                                title="삭제"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>

                          {editingMemoId === memo.id ? (
                            <div className="mt-1">
                              <textarea
                                value={editMemoContent}
                                onChange={(e) => setEditMemoContent(e.target.value)}
                                rows={3}
                                className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm resize-none focus:ring-blue-500 focus:border-blue-500"
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  onClick={() => { setEditingMemoId(null); setEditMemoContent(''); }}
                                  className="px-3 py-1 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                                >
                                  취소
                                </button>
                                <button
                                  onClick={() => handleMemoEdit(memo.id)}
                                  className="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
                                >
                                  저장
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-800 whitespace-pre-wrap">{memo.content}</p>
                          )}
                        </div>
                      ))}

                      {/* 더보기 버튼 */}
                      {memos.length < memosTotal && (
                        <div className="flex justify-center pt-2">
                          <button
                            onClick={() => {
                              const studentId = selectedMember?.user_id || selectedMember?.id;
                              if (studentId) loadMemos(studentId, memos.length);
                            }}
                            disabled={isLoadingMemos}
                            className="px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50"
                          >
                            더보기 ({memosTotal - memos.length}건 남음)
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">메모가 없습니다</p>
                      <p className="text-sm text-gray-400 mt-1">위에서 메모를 작성해보세요</p>
                    </div>
                  )}
                </div>
              )}

            {/* 모달 액션 버튼 */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 학부모 초대 모달 */}
      {showInviteModal && selectedStudent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 text-center flex items-center justify-center gap-2">
                <UserGroupIcon className="h-6 w-6 text-purple-600" />
                학부모 초대 링크 발송
              </h3>

              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong className="text-purple-700">{selectedStudent.name}</strong> 학생의 학부모를 초대합니다
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  학부모가 초대 링크로 가입하면 자동으로 {selectedStudent.name} 학생과 연결됩니다.
                </p>
              </div>

              {!inviteUrl ? (
                // Step 1: 등록된 번호 확인 + 발송
                <div className="mt-4 space-y-4">
                  <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
                    <p className="text-sm font-medium text-gray-800 mb-1">
                      등록된 학부모 연락처
                    </p>

                    {hasParentPhone(selectedStudent) ? (
                      <ul className="text-sm text-gray-700 space-y-1">
                        {selectedStudent.parent_phone_1 && (
                          <li>학부모 1: {selectedStudent.parent_phone_1}</li>
                        )}
                        {selectedStudent.parent_phone_2 && (
                          <li>학부모 2: {selectedStudent.parent_phone_2}</li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-sm text-red-600">
                        등록된 학부모 연락처가 없습니다. 회원 상세 화면에서 학부모 연락처를 먼저 등록해주세요.
                      </p>
                    )}

                    {hasParentPhone(selectedStudent) && (
                      <p className="mt-2 text-xs text-purple-700">
                        초대 링크는 등록된 학부모 연락처 중 우선순위 번호로 자동 발송됩니다.
                      </p>
                    )}
                  </div>

                  {inviteError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                      {inviteError}
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={closeInviteModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      disabled={inviteLoading}
                    >
                      닫기
                    </button>
                    <button
                      onClick={() => handleCreateInvite(selectedStudent)}
                      disabled={!hasParentPhone(selectedStudent) || inviteLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {inviteLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          발송 중...
                        </>
                      ) : (
                        '초대 링크 발송'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                // Step 2: 발송 완료 + 링크 표시/복사
                <div className="mt-4 space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-semibold text-green-800 mb-1">
                      초대 링크가 학부모에게 발송되었습니다.
                    </p>
                    <p className="text-xs text-green-700">
                      카카오톡 또는 문자 알림으로 초대장이 전송되었으며, 링크를 통해 가입하면 자동으로 학생과 연결됩니다.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      생성된 초대 링크
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={inviteUrl}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                      />
                      <button
                        onClick={handleCopyUrl}
                        className={`px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2 ${
                          copied
                            ? 'bg-green-600 text-white'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {copied ? (
                          <>
                            <CheckIcon className="h-4 w-4" />
                            복사됨
                          </>
                        ) : (
                          <>
                            <ClipboardDocumentIcon className="h-4 w-4" />
                            복사
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">
                      안내
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>학부모는 수신한 링크로 회원가입을 진행합니다.</li>
                      <li>SMS/카카오 인증 후 자동으로 {selectedStudent.name} 학생과 연결됩니다.</li>
                      <li>링크 유효기간: 7일</li>
                    </ul>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={closeInviteModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700"
                    >
                      확인
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 벌점 상세보기 — 예정 시각 / 실제 도착 시각 / 지각 시간 / 그날 입·퇴실 기록 */}
      {penaltyDetailId && (
        <PenaltyDetailModal
          recordId={penaltyDetailId}
          onClose={() => setPenaltyDetailId(null)}
        />
      )}
    </div>
  );
};

export default MemberManagement;
